import { phraseTempsPasse } from '../../lib/tempsMission';
import { totalLigneApresRemise } from '../../lib/remiseExtras';
import { RemiseExtraField } from './RemiseExtraField';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrouverCreneauModal } from './TrouverCreneauModal';
import { FiltreSalaries } from './FiltreSalaries';
import { MenuDeroulant } from '../ui/MenuDeroulant';
import { CalendarClock, ChevronLeft, ChevronRight, Plus, X, Clock, Trash2, Loader2, Users as UsersIcon, User, Phone, Mail, MapPin, Navigation, MessageSquare, Send, CheckCircle, PlayCircle, Bell, Link2, ClipboardList, NotebookText, Settings as SettingsIcon, FileText, MoreHorizontal } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '../../context/AuthContext';
import { moduleApi, notesApi, authApi } from '../../lib/api';
import { OrigineDemande } from './OrigineDemande';
import { SendCustomMessageModal } from '../ui/SendCustomMessageModal';
import { SendDocumentByEmailModal } from '../RdvPro/SendDocumentByEmailModal';
import { AdminCompletionModal } from './AdminCompletionModal';
import { StartPrestationModal } from './StartPrestationModal';
import { LinkedNotesPanel, LinkedNotesQuickModal } from '../Notes/LinkedNotesPanel';
import { PrestationNotificationsEnLignes } from './PrestationNotificationsPage';
import {
    WeeklyHours, DAY_LABELS, defaultWeeklyHours,
    PX_PER_HOUR, timeToMinutes, minutesToTime, formatDateInput, formatDateHuman, contactLabel,
    dayKeyForDate, dayShortLabel, packAppointments, EmailLogEntry, EMAIL_TYPE_LABELS, formatDureeMinutes, couleurSalarie,
} from './prestationPlanningShared';

interface PrestationCatalogItem {
    id: string;
    label: string;
    unit_price_ttc: number;
    tax_rate: number;
    show_tva: boolean;
    default_duration_minutes: number;
    client_type_scope: 'TOUS' | 'PROFESSIONNEL' | 'PRIVÉ';
}

interface Employee {
    peut_etre_responsable?: number | boolean;
    id: string;
    first_name: string;
    last_name: string;
    job_post_title?: string;
    couleur_planning?: string | null;
}

interface CrmSuggestion {
    id: string;
    type: 'PRIVÉ' | 'PROFESSIONNEL';
    first_name?: string;
    last_name?: string;
    company_name?: string;
    phone?: string;
    email?: string;
    address?: string;
    postal_code?: string;
    city?: string;
    country?: string;
}

interface Appointment {
    id: string;
    crm_contact_id: string | null;
    crm_first_name?: string;
    crm_last_name?: string;
    crm_company_name?: string;
    crm_phone?: string;
    crm_email?: string;
    crm_address?: string;
    crm_postal_code?: string;
    crm_city?: string;
    crm_country?: string;
    status?: string;
    location_type?: 'domicile' | 'entreprise';
    total_amount_ttc?: number;
    amount_already_paid?: number;
    km_fee_amount?: number;
    employee_ids: string[];
    lead_employee_id?: string | null;
    // Vendeur qui a fait signer le devis sur place (démarchage à domicile) : « Pris par … » (2026-10-22).
    commercial_nom?: string;
    prestation_catalog_item_id: string | null;
    prestation_label: string;
    quantity: number;
    unit_price_ttc: number;
    tax_rate: number;
    catalog_items?: { catalog_item_id: string | null; label: string; quantity: number; unit_price_ttc: number; tax_rate: number; unit_duration_minutes: number }[];
    unit_duration_minutes: number;
    computed_duration_minutes: number;
    appointment_date: string;
    start_time: string;
    payment_status: string;
    payment_method: string;
    payment_breakdown?: { method: string; amount: number }[];
    payment_intent_id?: string | null;
    completed_at?: string;
    started_at?: string;
    photo_before_url?: string;
    facture_id?: string;
    formula_occurrence_number?: number | null;
    completion_report?: { label: string; catalog_item_id: string | null; quantity_planned: number; quantity_done: number }[];
    payment_gate_amount_due?: number | null;
    notes: string;
}

const PAYMENT_STATUS_LABELS: Record<string, string> = {
    a_encaisser: 'À encaisser',
    encaisse: 'Encaissé',
    a_regler_sous_x_jours: 'À régler (délai)',
};

const employeeNamesLabel = (appt: Appointment, employees: Employee[]) => {
    return (appt.employee_ids || [])
        .map((id) => {
            const e = employees.find((x) => x.id === id);
            return e ? `${e.first_name} ${e.last_name}`.trim() : '';
        })
        .filter(Boolean)
        .join(', ');
};

function AppointmentModal({
    initial, catalog, employees, date, weeklyHours, presetTime, presetEmployeeIds, onClose, onSave, onDelete,
}: {
    initial: Appointment | null;
    catalog: PrestationCatalogItem[];
    employees: Employee[];
    date: string;
    weeklyHours: WeeklyHours;
    // Venus de « Trouver un créneau » : heure et salariés déjà choisis.
    presetTime?: string;
    presetEmployeeIds?: string[];
    onClose: () => void;
    onSave: (payload: any) => Promise<void>;
    onDelete?: () => Promise<void>;
}) {
    const { user } = useAuth();
    const locationMode = (user as any)?.prestation_location_mode || 'domicile';
    const [appointmentDate, setAppointmentDate] = useState(initial?.appointment_date || date);
    const dayHoursForModal = weeklyHours[dayKeyForDate(appointmentDate)];
    const minTime = dayHoursForModal.opening;
    const maxTime = dayHoursForModal.closing;
    const [crmContactId, setCrmContactId] = useState(initial?.crm_contact_id || '');
    const [crmType, setCrmType] = useState<'PRIVÉ' | 'PROFESSIONNEL'>('PRIVÉ');
    const [firstName, setFirstName] = useState(initial?.crm_first_name || '');
    const [lastName, setLastName] = useState(initial?.crm_last_name || '');
    const [companyName, setCompanyName] = useState(initial?.crm_company_name || '');
    const [phone, setPhone] = useState(initial?.crm_phone || '');
    const [email, setEmail] = useState(initial?.crm_email || '');
    const [address, setAddress] = useState(initial?.crm_address || '');
    const [postalCode, setPostalCode] = useState(initial?.crm_postal_code || '');
    const [city, setCity] = useState(initial?.crm_city || '');
    const [lookupField, setLookupField] = useState<'firstName' | 'lastName' | 'companyName' | null>(null);
    const [lookupTerm, setLookupTerm] = useState('');
    const [suggestions, setSuggestions] = useState<CrmSuggestion[]>([]);
    const [searching, setSearching] = useState(false);

    // remise_auto / offre_auto : réduction posée par le calcul automatique (lots, options, offres) — jamais envoyés au serveur
    // comme tels, ils disent seulement à l'écran ce qu'il peut encore recalculer sans écraser une remise saisie à la main.
    interface AppointmentCatalogItem { catalog_item_id: string | null; label: string; quantity: number; unit_price_ttc: number; tax_rate: number; unit_duration_minutes: number; discount_type?: 'percent' | 'amount' | 'offered' | null; discount_value?: number; offered_quantity?: number; remise_auto?: boolean; offre_auto?: string; }
    const initialCatalogItems: AppointmentCatalogItem[] = Array.isArray(initial?.catalog_items) && initial.catalog_items.length > 0
        ? initial.catalog_items
        : (initial?.prestation_label ? [{
            catalog_item_id: initial?.prestation_catalog_item_id || null,
            label: initial.prestation_label,
            quantity: initial?.quantity ?? 1,
            unit_price_ttc: initial?.unit_price_ttc ?? 0,
            tax_rate: initial?.tax_rate ?? 0,
            unit_duration_minutes: initial?.unit_duration_minutes ?? 0,
        }] : []);
    const [prestationItems, setPrestationItems] = useState<AppointmentCatalogItem[]>(initialCatalogItems);
    const [employeeIds, setEmployeeIds] = useState<string[]>(initial?.employee_ids || presetEmployeeIds || []);
    // Responsable sur place : celui qui commence et termine la prestation (photos, signature) quand l'établissement le décide.
    const [leadEmployeeId, setLeadEmployeeId] = useState<string>(initial?.lead_employee_id || '');
    // Nom du rôle choisi par l'établissement (Paramètres > Clôture des prestations).
    const [roleLabel, setRoleLabel] = useState('Responsable');
    // Plusieurs salariés et personne de désigné : la première personne marquée « peut diriger une équipe » est proposée d'office.
    useEffect(() => {
        if (employeeIds.length < 2 || leadEmployeeId) return;
        const chef = employees.find((emp) => employeeIds.includes(emp.id) && Number(emp.peut_etre_responsable) === 1);
        if (chef) setLeadEmployeeId(chef.id);
    }, [employeeIds, employees, leadEmployeeId]);
    useEffect(() => { moduleApi.getPrestationClotureReglages().then((r: any) => { if (r?.role_label) setRoleLabel(String(r.role_label)); }).catch(() => {}); }, []);
    const [startTime, setStartTime] = useState(initial?.start_time || presetTime || '09:00');
    const [paymentStatus, setPaymentStatus] = useState(initial?.payment_status || 'a_encaisser');
    // Paiement demandé au client à la création (2026-10-16) : la règle générale vient de Paramètres > Application.
    const [regleReservation, setRegleReservation] = useState<{ payment_type: string; payment_deposit_percent: number; payment_deadline_hours: number } | null>(null);
    const [demanderPaiement, setDemanderPaiement] = useState(true);
    // Prestation déjà faite hors plateforme (2026-11-03) : rendez-vous saisi après coup, aucun e-mail ni lien de paiement.
    const [dejaRealise, setDejaRealise] = useState(false);
    useEffect(() => {
        if (initial) return;
        moduleApi.getPrestationPlanningSettings()
            .then((r: any) => { if (r?.booking_rules) setRegleReservation(r.booking_rules); })
            .catch(() => {});
    }, []);
    const [paymentMethod, setPaymentMethod] = useState(initial?.payment_method || '');
    const [notes, setNotes] = useState(initial?.notes || '');
    const [locationType, setLocationType] = useState<'domicile' | 'entreprise'>(
        initial?.location_type || (locationMode === 'entreprise' ? 'entreprise' : 'domicile')
    );
    const [recurrenceFrequency, setRecurrenceFrequency] = useState<'none' | 'weekly' | 'biweekly' | 'monthly'>('none');
    const [recurrenceOccurrences, setRecurrenceOccurrences] = useState(8);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Formules avec engagement (2026-09-02) — dès qu'un contact CRM connu est sélectionné, on regarde s'il a
    // une formule active en cours pour lui proposer de rattacher ce rendez-vous (occurrence suivante,
    // réduction/gratuité automatique à la clôture, jamais ici).
    const [contactSubscriptions, setContactSubscriptions] = useState<any[]>([]);
    const [formulaSubscriptionId, setFormulaSubscriptionId] = useState((initial as any)?.formula_subscription_id || '');
    useEffect(() => {
        if (!crmContactId) { setContactSubscriptions([]); setFormulaSubscriptionId(''); return; }
        moduleApi.getContactFormulaSubscriptions(crmContactId)
            .then((d: any) => setContactSubscriptions((Array.isArray(d) ? d : []).filter((s: any) => s.status === 'active')))
            .catch(() => setContactSubscriptions([]));
    }, [crmContactId]);
    const selectedSubscription = contactSubscriptions.find((s) => s.id === formulaSubscriptionId) || null;
    // Dossier de suivi du client (2026-10-19) : prestations restant à planifier, avec le passage et les quantités à réaliser lors de cette visite.
    interface LigneDossier { catalog_item_id: string | null; label: string; quantity: number; unit_price_ttc: number; tax_rate: number; unit_duration_minutes: number }
    interface PassageDossier { numero: number; periode: string; reste: number; ancien: boolean; avancement: { total_prevu: number; total_fait: number; pourcentage: number } | null; lignes: LigneDossier[] }
    interface DossierAPlanifier { id: string; label: string; stage: string; sans_formule: boolean; formula_subscription_id: string | null; passages: PassageDossier[] }
    const [dossierClient, setDossierClient] = useState<DossierAPlanifier | null>(null);
    const [passageDossier, setPassageDossier] = useState<number | null>(null);
    const [partsDossier, setPartsDossier] = useState<Record<string, number>>({});
    const [dossierUtilise, setDossierUtilise] = useState<string>('');
    useEffect(() => {
        setDossierClient(null); setPassageDossier(null); setPartsDossier({}); setDossierUtilise('');
        if (!crmContactId || initial) return;
        let annule = false;
        moduleApi.getPrestationDossierAPlanifier(crmContactId)
            .then((r: any) => {
                if (annule || !r?.dossier) return;
                const d: DossierAPlanifier = r.dossier;
                setDossierClient(d);
                const avecReste = d.passages.filter((p) => p.reste > 0);
                setPassageDossier((avecReste[0] || d.passages[0] || { numero: null }).numero ?? null);
            })
            .catch(() => {});
        return () => { annule = true; };
    }, [crmContactId]);
    const passageDossierActuel = dossierClient?.passages.find((p) => p.numero === passageDossier) || null;
    const cleLigneDossier = (l: LigneDossier) => String(l.catalog_item_id ?? l.label);
    const utiliserDossier = () => {
        if (!dossierClient || !passageDossierActuel) return;
        const items = passageDossierActuel.lignes
            .map((l) => ({ catalog_item_id: l.catalog_item_id, label: l.label, quantity: Math.max(0, Math.min(l.quantity, partsDossier[cleLigneDossier(l)] ?? l.quantity)), unit_price_ttc: l.unit_price_ttc, tax_rate: l.tax_rate, unit_duration_minutes: l.unit_duration_minutes }))
            .filter((it) => it.quantity > 0);
        if (items.length === 0) { setError('Choisissez au moins une prestation pour cette visite.'); return; }
        setError('');
        setPrestationItems(items as any);
        // Les frais kilométriques du devis font foi : le calcul automatique ne doit ni les retirer (adresse ou lieu qui ne permet pas
        // d'estimer le trajet) ni les recalculer — sinon le rendez-vous est facturé moins cher que le devis signé.
        if ((items as AppointmentCatalogItem[]).some(estLigneKm)) setKmRetire(true);
        setFormulaSubscriptionId(dossierClient.formula_subscription_id || '');
        setDossierUtilise(`${dossierClient.label}${dossierClient.formula_subscription_id ? ` · passage ${passageDossierActuel.numero}` : ''}`);
    };
    const addFormulaItemsToPrestation = (sub: any) => {
        const items = Array.isArray(sub?.formula_items) ? sub.formula_items : [];
        items.forEach((it: any) => {
            if (!prestationItems.some((p) => p.catalog_item_id === it.catalog_item_id)) addPrestationItem(it.catalog_item_id);
        });
    };

    useEffect(() => {
        const q = lookupTerm.trim();
        if (q.length < 2) { setSuggestions([]); setSearching(false); return; }
        let cancelled = false;
        setSearching(true);
        const timer = setTimeout(async () => {
            try {
                const items = await moduleApi.searchCRMContacts(q);
                if (!cancelled) setSuggestions(Array.isArray(items) ? items : []);
            } catch {
                if (!cancelled) setSuggestions([]);
            } finally {
                if (!cancelled) setSearching(false);
            }
        }, 250);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [lookupTerm]);

    const triggerLookup = (field: 'firstName' | 'lastName' | 'companyName', value: string) => {
        setLookupField(field);
        setLookupTerm(value);
    };

    const applyCRMContact = (s: CrmSuggestion) => {
        if (s.type === 'PRIVÉ' || s.type === 'PROFESSIONNEL') setCrmType(s.type);
        setFirstName(s.first_name || '');
        setLastName(s.last_name || '');
        setCompanyName(s.company_name || '');
        setPhone(s.phone || '');
        setEmail(s.email || '');
        setAddress(s.address || '');
        setPostalCode(s.postal_code || '');
        setCity(s.city || '');
        setCrmContactId(s.id);
        setSuggestions([]);
        setLookupTerm('');
        setLookupField(null);
    };

    // « À partir d'un dossier » (refonte Dossiers clients) : les dossiers dont le devis est accepté et qui ont encore un passage à
    // planifier. Choisir un dossier remplit le client ; le reste (prestations du passage, formule) se remplit par la logique existante.
    interface DossierProposé { dossier_id: string; label: string; type: string; sans_formule: boolean; total_passages: number; passages_faits: number; prochain_passage: number; prochain_periode: string; crm_contact_id: string; contact: any }
    const [dossiersProposes, setDossiersProposes] = useState<DossierProposé[]>([]);
    useEffect(() => {
        if (initial) return;
        let annule = false;
        moduleApi.getPrestationDossiersAPlanifier()
            .then((r: any) => { if (!annule && Array.isArray(r?.dossiers)) setDossiersProposes(r.dossiers); })
            .catch(() => {});
        return () => { annule = true; };
    }, []);
    const choisirDossier = (d: DossierProposé) => {
        applyCRMContact({ id: d.crm_contact_id, type: d.type, ...(d.contact || {}) } as any);
    };

    const renderSuggestions = (field: 'firstName' | 'lastName' | 'companyName') => {
        if (lookupField !== field) return null;
        if (!searching && suggestions.length === 0) return null;
        return (
            <div className="absolute z-10 mt-1 w-full bg-surface border border-border rounded-xl shadow-xl max-h-48 overflow-y-auto">
                {searching && <div className="px-3 py-2 text-[11px] text-content-muted">Recherche CRM...</div>}
                {!searching && suggestions.map((s) => (
                    <button key={s.id} type="button" onMouseDown={() => applyCRMContact(s)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-surface-soft transition-colors text-content">
                        <div className="font-semibold truncate">{s.type === 'PROFESSIONNEL' ? (s.company_name || 'Entreprise') : [s.first_name, s.last_name].filter(Boolean).join(' ')}</div>
                        {s.phone && <div className="text-[11px] text-content-muted">{s.phone}</div>}
                    </button>
                ))}
            </div>
        );
    };

    const addPrestationItem = (catalogItemId: string) => {
        const found = catalog.find((i) => i.id === catalogItemId);
        if (!found) return;
        setCalculAuto(true);
        setPrestationItems((prev) => [...prev, {
            catalog_item_id: found.id,
            label: found.label,
            quantity: 1,
            unit_price_ttc: found.unit_price_ttc,
            tax_rate: found.show_tva ? found.tax_rate : 0,
            unit_duration_minutes: found.default_duration_minutes,
        }]);
    };
    const updatePrestationItem = (idx: number, patch: Partial<AppointmentCatalogItem>) => {
        setCalculAuto(true);
        setPrestationItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
    };
    const removePrestationItem = (idx: number) => {
        setCalculAuto(true);
        // Une option offerte d'office qu'on retire ne revient pas toute seule.
        const cle = prestationItems[idx]?.offre_auto;
        if (cle) setOffresRetirees((r) => [...r, cle]);
        if (prestationItems[idx] && estLigneKm(prestationItems[idx])) setKmRetire(true);
        setPrestationItems((prev) => prev.filter((_, i) => i !== idx));
    };

    // CALCUL AUTOMATIQUE (2026-10-22, demande d'Emeni) : dès que l'adresse est saisie, les frais kilométriques sont calculés et
    // ajoutés (offerts au-delà du seuil réglé en Facturation) ; les réductions et offres des tarifs (lots, options en %, offres
    // par quantité) s'appliquent toutes seules aux prestations, avec le même calcul que le devis. Un rendez-vous existant n'est
    // recalculé que si on modifie ses prestations ou son adresse ; une remise saisie à la main n'est jamais écrasée.
    const [calculAuto, setCalculAuto] = useState(!initial);
    const [offresRetirees, setOffresRetirees] = useState<string[]>([]);
    const estLigneKm = (it: AppointmentCatalogItem) => !it.catalog_item_id && /^Frais kilom/i.test(it.label || '');
    const [kmBase, setKmBase] = useState<{ fee: number; label: string; seuil: number; zone: boolean } | null>(null);
    const [kmInfo, setKmInfo] = useState('');
    // Ligne de frais km supprimée à la main : on ne la remet pas.
    const [kmRetire, setKmRetire] = useState(false);
    useEffect(() => {
        if (!calculAuto || kmRetire) return;
        if (locationType !== 'domicile' || !address.trim() || (!postalCode.trim() && !city.trim())) { setKmBase(null); setKmInfo(''); return; }
        let annule = false;
        const t = setTimeout(() => {
            setKmInfo('Calcul des frais kilométriques…');
            moduleApi.estimatePrestationDevisKmFee({ address, postal_code: postalCode, city, type: crmType })
                .then((r: any) => {
                    if (annule) return;
                    if (r?.error === 'itineraire_indisponible') { setKmInfo("Le calcul d'itinéraire ne répond pas pour le moment."); return; }
                    if (!r || r.error || (!r.fee && !r.zone_gratuite)) { setKmBase(null); setKmInfo(''); return; }
                    setKmBase(r.zone_gratuite
                        ? { fee: 0, label: `Frais kilométriques (${r.trajet_km} km, dans notre zone de ${r.rayon_km} km) — Offerts`, seuil: 0, zone: true }
                        : { fee: Number(r.fee) || 0, label: `Frais kilométriques (${r.distance_km} km ${r.aller_retour ? 'aller-retour' : 'aller'})`, seuil: Number(r.waiver_threshold_ttc) || 0, zone: false });
                    setKmInfo('');
                })
                .catch(() => { if (!annule) setKmInfo('Frais kilométriques : calcul impossible pour le moment.'); });
        }, 700);
        return () => { annule = true; clearTimeout(t); };
    }, [calculAuto, kmRetire, address, postalCode, city, locationType, crmType]);
    // Ligne « Frais kilométriques » : posée, mise à jour, ou passée en « offerts » dès que les prestations atteignent le seuil.
    const sousTotalHorsKm = prestationItems.filter((it) => !estLigneKm(it)).reduce((s, it) => s + totalLigneApresRemise(it), 0);
    useEffect(() => {
        if (!calculAuto || kmRetire) return;
        setPrestationItems((prev) => {
            const sans = prev.filter((it) => !estLigneKm(it));
            if (!kmBase) return sans.length === prev.length ? prev : sans;
            const offert = !kmBase.zone && kmBase.seuil > 0 && sousTotalHorsKm >= kmBase.seuil;
            const taxe = Number(sans[0]?.tax_rate) || 0;
            const ligne: AppointmentCatalogItem = {
                catalog_item_id: null, label: offert ? `${kmBase.label} — Offerts` : kmBase.label, quantity: 1,
                unit_price_ttc: kmBase.fee, tax_rate: taxe, unit_duration_minutes: 0,
                ...(offert ? { discount_type: 'offered' as const, discount_value: 0 } : {}),
            };
            const actuelle = prev.find(estLigneKm);
            if (actuelle && JSON.stringify(actuelle) === JSON.stringify(ligne)) return prev;
            return [...sans, ligne];
        });
    }, [calculAuto, kmRetire, kmBase, sousTotalHorsKm]);

    // Réductions automatiques : aperçu du devis (computeDevisTotals) sur les prestations du catalogue, puis report de chaque
    // réduction sur sa ligne (remise %, € ou offert), comme elle figurera sur la facture.
    const signatureRemises = JSON.stringify([crmType, prestationItems.filter((it) => it.catalog_item_id && !it.offre_auto).map((it) => [it.catalog_item_id, it.quantity, !!it.remise_auto || !it.discount_type])]);
    useEffect(() => {
        if (!calculAuto || dossierUtilise) return;
        const items = prestationItems.filter((it) => it.catalog_item_id && !it.offre_auto);
        if (items.length === 0) return;
        let annule = false;
        const t = setTimeout(async () => {
            try {
                const base = items.map((it) => ({ catalog_item_id: it.catalog_item_id, quantity: Math.max(1, Number(it.quantity) || 1), label: it.label, unit_price_ttc: it.unit_price_ttc, tax_rate: it.tax_rate }));
                let r: any = await moduleApi.previewPrestationDevis({ items: base, type: crmType });
                const auto: { cle: string; option_item_id: string; quantite: number }[] = (Array.isArray(r?.offres_auto) ? r.offres_auto : []).filter((a: any) => !offresRetirees.includes(a.cle));
                if (auto.length) r = await moduleApi.previewPrestationDevis({ items: [...base, ...auto.map((a) => ({ catalog_item_id: a.option_item_id, quantity: Math.max(1, a.quantite), offre_auto: a.cle }))], type: crmType });
                if (annule || !r || r.error || !Array.isArray(r.lines)) return;
                const lignes: any[] = r.lines;
                const arr = (n: number) => Math.round(n * 100) / 100;
                setPrestationItems((prev) => {
                    const dejaVues: Record<string, number> = {};
                    const suivant = prev.filter((it) => !it.offre_auto || auto.some((a) => a.cle === it.offre_auto)).map((it) => {
                        if (!it.catalog_item_id || it.offre_auto) return it;
                        // Remise saisie à la main : on n'y touche pas.
                        if (it.discount_type && !it.remise_auto) return it;
                        const rang = dejaVues[it.catalog_item_id] = (dejaVues[it.catalog_item_id] ?? -1) + 1;
                        const idx = lignes.map((l, i) => ({ l, i })).filter(({ l }) => !l.is_discount_line && !l.offre_auto && String(l.catalog_item_id) === String(it.catalog_item_id))[rang]?.i;
                        if (idx === undefined) return it;
                        const l = lignes[idx];
                        // Réductions posées sous cette ligne (option offerte ou remisée par quantité).
                        let offres = 0;
                        for (let k = idx + 1; k < lignes.length && lignes[k].is_discount_line && String(lignes[k].catalog_item_id) === String(it.catalog_item_id); k++) offres += -Number(lignes[k].line_ttc) || 0;
                        const prixListe = Number(l.list_unit_price_ttc ?? l.unit_price_ttc) || 0;
                        const q = Math.max(1, Number(it.quantity) || 1);
                        const net = arr(Number(l.line_ttc) - offres);
                        const remise = arr(prixListe * q - net);
                        const vol = Number(l.volume_discount_pct) || 0;
                        // Temps de la prestation saisi dans Réductions et options : il prime sur celui du catalogue.
                        const duree = l.duree_unitaire !== undefined ? { unit_duration_minutes: Number(l.duree_unitaire) || 0 } : {};
                        const nouveau: AppointmentCatalogItem = remise > 0.004
                            ? (vol > 0 && offres === 0
                                ? { ...it, ...duree, unit_price_ttc: prixListe, discount_type: 'percent', discount_value: vol, offered_quantity: 0, remise_auto: true }
                                : { ...it, ...duree, unit_price_ttc: prixListe, discount_type: 'amount', discount_value: remise, offered_quantity: 0, remise_auto: true })
                            : { ...it, ...duree, unit_price_ttc: prixListe, discount_type: null, discount_value: 0, remise_auto: it.remise_auto ? false : it.remise_auto };
                        return nouveau;
                    });
                    // Options offertes d'office (« dès 2 canapés, la désinfection est offerte sur l'un des deux »).
                    for (let i = 0; i < lignes.length; i++) {
                        const l = lignes[i];
                        if (!l.offre_auto) continue;
                        const a = auto.find((x) => String(x.option_item_id) === String(l.catalog_item_id) && !suivant.some((it) => it.offre_auto === x.cle));
                        if (!a) continue;
                        const ci = catalog.find((c) => c.id === a.option_item_id);
                        suivant.push({ catalog_item_id: a.option_item_id, label: lignes[i + 1]?.label || l.label, quantity: Math.max(1, Number(l.quantity) || 1), unit_price_ttc: Number(l.unit_price_ttc) || 0, tax_rate: Number(l.tax_rate) || 0, unit_duration_minutes: Number(ci?.default_duration_minutes) || 0, discount_type: 'offered', discount_value: 0, offre_auto: a.cle });
                    }
                    return JSON.stringify(suivant) === JSON.stringify(prev) ? prev : suivant;
                });
            } catch { /* le rendez-vous reste au tarif saisi */ }
        }, 400);
        return () => { annule = true; clearTimeout(t); };
    }, [calculAuto, dossierUtilise, signatureRemises, offresRetirees.join('|')]);

    const toggleEmployee = (id: string) => {
        setEmployeeIds((prev) => prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]);
        setLeadEmployeeId((lead) => (lead === id ? '' : lead));
    };

    // Durée du rendez-vous = somme(quantité × durée unitaire configurée sur la prestation) ÷ nombre de
    // salariés assignés (ex. 350 chaises × 6 min ÷ 2 salariés = 17 h 30).
    const totalPrestationDuration = prestationItems.reduce((sum, it) => sum + (Number(it.quantity || 0) * Number(it.unit_duration_minutes || 0)), 0);
    const computedDuration = employeeIds.length > 0 ? totalPrestationDuration / employeeIds.length : 0;
    const prestationsSansDuree = prestationItems.filter((it) => !estLigneKm(it) && Number(it.unit_duration_minutes || 0) <= 0).map((it) => it.label);

    const handleSubmit = async () => {
        setError('');
        if (prestationItems.length === 0) { setError('Sélectionnez au moins une prestation.'); return; }
        if (employeeIds.length === 0) { setError('Assignez au moins un employé.'); return; }
        if (!firstName.trim() && !lastName.trim() && !companyName.trim()) { setError('Renseignez le client (nom ou société).'); return; }
        setSaving(true);
        try {
            await onSave({
                crm_contact_id: crmContactId || undefined,
                crm_type: crmType,
                first_name: firstName,
                last_name: lastName,
                company_name: companyName,
                phone,
                email,
                address,
                postal_code: postalCode,
                city,
                employee_ids: employeeIds,
                lead_employee_id: employeeIds.length > 1 && employeeIds.includes(leadEmployeeId) ? leadEmployeeId : null,
                catalog_items: prestationItems,
                appointment_date: appointmentDate,
                start_time: startTime,
                payment_status: paymentStatus,
                payment_method: paymentMethod,
                ...(!initial ? { request_payment: demanderPaiement && !dejaRealise, deja_realise: dejaRealise } : {}),
                notes,
                location_type: locationType,
                formula_subscription_id: formulaSubscriptionId || undefined,
                ...(dossierUtilise && dossierClient?.formula_subscription_id && passageDossier ? { formula_occurrence_number: passageDossier } : {}),
                // Devis simple (sans formule) : la visite se rattache au dossier, sinon elle n'apparaît pas dans son suivi.
                ...(dossierUtilise && dossierClient && !dossierClient.formula_subscription_id ? { source_dossier_id: dossierClient.id } : {}),
                ...(recurrenceFrequency !== 'none' ? { recurrence: { frequency: recurrenceFrequency, occurrences: recurrenceOccurrences } } : {}),
            });
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-xl bg-surface border border-border rounded-3xl max-h-[90vh] shadow-2xl flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b border-border shrink-0">
                    <h3 className="text-lg font-bold text-content flex items-center gap-2">
                        <CalendarClock size={18} className="text-brand-600 dark:text-brand-400" />
                        {initial?.status === 'demande' ? 'Confirmer la demande de réservation' : initial ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}
                    </h3>
                    <button onClick={onClose} className="p-2 text-content-muted hover:text-content rounded-xl hover:bg-surface-soft"><X size={20} /></button>
                </div>
                {initial?.status === 'demande' && (
                    <div className="mx-6 mt-4 p-3 rounded-xl bg-warning-500/10 border border-warning-500/20 text-xs text-warning-500 font-semibold">
                        Demande reçue via le widget de réservation en ligne — assignez au moins un salarié puis validez pour confirmer le rendez-vous au client final.
                    </div>
                )}
                {initial?.status === 'demande' && <OrigineDemande appointmentId={initial.id} />}
                <div className="p-6 space-y-5 overflow-y-auto">
                    {!initial && !crmContactId && dossiersProposes.length > 0 && (
                        <div className="space-y-2" role="group" aria-label="À partir d'un dossier">
                            <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest">À partir d'un dossier</p>
                            <div className="space-y-1.5">
                                {dossiersProposes.slice(0, 5).map((d) => (
                                    <button key={d.dossier_id} type="button" onClick={() => choisirDossier(d)}
                                        className="w-full flex items-center justify-between gap-3 text-left px-3.5 py-2.5 rounded-xl border border-border bg-surface hover:border-content transition-colors">
                                        <span className="min-w-0">
                                            <span className="block text-sm font-bold text-content truncate">{d.label}</span>
                                            <span className="block text-[11px] text-content-muted truncate">
                                                {d.sans_formule ? 'Prestations du devis à planifier' : `Passage ${d.prochain_passage}${d.prochain_periode ? ` — ${d.prochain_periode}` : ''} à planifier`}
                                            </span>
                                        </span>
                                        {!d.sans_formule && d.total_passages > 1 && <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded bg-surface-soft border border-border text-content-muted">{d.passages_faits} sur {d.total_passages}</span>}
                                    </button>
                                ))}
                            </div>
                            <p className="text-[11px] text-content-muted">Le devis est repris pour vous. Sinon, saisissez le client ci-dessous (rendez-vous express).</p>
                        </div>
                    )}
                    <div className="space-y-2">
                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Client</label>
                        <div className="flex items-center gap-4">
                            <button type="button" onClick={() => setCrmType('PRIVÉ')}
                                className={`text-xs font-bold uppercase tracking-widest pb-1 border-b-2 transition-all ${crmType === 'PRIVÉ' ? 'border-brand-600 text-content' : 'border-transparent text-content-muted hover:text-content'}`}>
                                Particulier
                            </button>
                            <button type="button" onClick={() => setCrmType('PROFESSIONNEL')}
                                className={`text-xs font-bold uppercase tracking-widest pb-1 border-b-2 transition-all ${crmType === 'PROFESSIONNEL' ? 'border-brand-600 text-content' : 'border-transparent text-content-muted hover:text-content'}`}>
                                Professionnel
                            </button>
                        </div>
                    </div>

                    {crmType === 'PRIVÉ' ? (
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1 relative">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Prénom</label>
                                <input type="text" value={firstName}
                                    onChange={(e) => { setFirstName(e.target.value); setCrmContactId(''); triggerLookup('firstName', e.target.value); }}
                                    onBlur={() => setTimeout(() => setLookupField(null), 150)}
                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                {renderSuggestions('firstName')}
                            </div>
                            <div className="space-y-1 relative">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Nom</label>
                                <input type="text" value={lastName}
                                    onChange={(e) => { setLastName(e.target.value); setCrmContactId(''); triggerLookup('lastName', e.target.value); }}
                                    onBlur={() => setTimeout(() => setLookupField(null), 150)}
                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                {renderSuggestions('lastName')}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-1 relative">
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Société</label>
                            <input type="text" value={companyName}
                                onChange={(e) => { setCompanyName(e.target.value); setCrmContactId(''); triggerLookup('companyName', e.target.value); }}
                                onBlur={() => setTimeout(() => setLookupField(null), 150)}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                            {renderSuggestions('companyName')}
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Téléphone</label>
                            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Email</label>
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                                placeholder="Nécessaire pour les rappels par email"
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                        </div>
                    </div>

                    {locationMode === 'les_deux' && (
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Lieu du rendez-vous</label>
                            <div className="grid grid-cols-2 gap-2">
                                <button type="button" onClick={() => setLocationType('domicile')}
                                    className={`px-3 py-2.5 rounded-xl text-xs font-bold border transition-all ${locationType === 'domicile' ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                    Chez le client
                                </button>
                                <button type="button" onClick={() => setLocationType('entreprise')}
                                    className={`px-3 py-2.5 rounded-xl text-xs font-bold border transition-all ${locationType === 'entreprise' ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                    Dans l'entreprise
                                </button>
                            </div>
                        </div>
                    )}

                    {locationType === 'domicile' && (
                        <>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Adresse du rendez-vous</label>
                                <input type="text" value={address} onChange={(e) => { setAddress(e.target.value); setCalculAuto(true); }}
                                    placeholder="N° et rue"
                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Code postal</label>
                                    <input type="text" value={postalCode} onChange={(e) => { setPostalCode(e.target.value); setCalculAuto(true); }}
                                        className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Ville</label>
                                    <input type="text" value={city} onChange={(e) => { setCity(e.target.value); setCalculAuto(true); }}
                                        className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                </div>
                            </div>
                        </>
                    )}

                    {contactSubscriptions.length > 0 && (
                        <div className="space-y-1.5">
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Formule (optionnel)</label>
                            <select value={formulaSubscriptionId} onChange={(e) => setFormulaSubscriptionId(e.target.value)}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500">
                                <option value="">Aucune</option>
                                {contactSubscriptions.map((s) => (
                                    <option key={s.id} value={s.id}>{s.formula_name} — occurrence {Number(s.occurrences_used || 0) + 1}/{s.occurrences_total}</option>
                                ))}
                            </select>
                            {selectedSubscription && (
                                <div className="flex items-center justify-between gap-2 text-xs text-content-muted bg-brand-500/10 border border-brand-500/20 rounded-xl px-3 py-2">
                                    <span>
                                        Occurrence {Number(selectedSubscription.occurrences_used || 0) + 1}{Number((selectedSubscription as any).passages_libres) ? '' : `/${selectedSubscription.occurrences_total}`}
                                        {(() => {
                                            // Chaque prestation de la formule porte sa propre règle — on résume ici ce qui
                                            // s'appliquera à CETTE occurrence, ligne par ligne (appliqué à la clôture).
                                            const occurrenceNumber = Number(selectedSubscription.occurrences_used || 0) + 1;
                                            const items = Array.isArray(selectedSubscription.formula_items) ? selectedSubscription.formula_items : [];
                                            const freeLabels = items.filter((it: any) => (it.free_occurrence_numbers || []).includes(occurrenceNumber)).map((it: any) => it.label);
                                            const discountedLabels = items.filter((it: any) => !(it.free_occurrence_numbers || []).includes(occurrenceNumber) && it.discount_type !== 'none' && it.discount_value > 0).map((it: any) => it.label);
                                            if (freeLabels.length === 0 && discountedLabels.length === 0) return null;
                                            const parts = [];
                                            if (freeLabels.length > 0) parts.push(`${freeLabels.join(', ')} offert${freeLabels.length > 1 ? 's' : ''}`);
                                            if (discountedLabels.length > 0) parts.push(`réduction sur ${discountedLabels.join(', ')}`);
                                            return ` — ${parts.join(' · ')} (appliqué à la clôture)`;
                                        })()}
                                    </span>
                                    <button type="button" onClick={() => addFormulaItemsToPrestation(selectedSubscription)} className="font-bold text-brand-600 dark:text-brand-400 hover:underline shrink-0">
                                        + Ajouter les prestations de la formule
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {dossierClient && dossierClient.passages.length > 0 && (
                        <div className="space-y-2.5 p-3.5 rounded-2xl bg-brand-500/5 border border-brand-500/25">
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-widest text-content-muted">Dossier en cours de ce client</p>
                                <p className="text-sm font-bold text-content">{dossierClient.label}</p>
                            </div>
                            {dossierClient.passages.length > 1 && (
                                <select value={passageDossier ?? ''} onChange={(e) => { setPassageDossier(Number(e.target.value)); setPartsDossier({}); setDossierUtilise(''); }}
                                    className="w-full bg-surface border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" aria-label="Passage du dossier">
                                    {dossierClient.passages.map((p) => (
                                        <option key={p.numero} value={p.numero}>{dossierClient.sans_formule ? 'Prestations du devis' : `Passage ${p.numero}${p.periode ? ` — ${p.periode}` : ''}`}{p.reste > 0 ? ` · reste ${p.reste}` : ' · tout est planifié'}</option>
                                    ))}
                                </select>
                            )}
                            {passageDossierActuel && passageDossierActuel.reste > 0 ? (
                                <>
                                    {passageDossierActuel.avancement && (
                                        <p className="text-[11px] text-content-muted">Déjà réalisé : <b className="text-content">{passageDossierActuel.avancement.total_fait} sur {passageDossierActuel.avancement.total_prevu}</b></p>
                                    )}
                                    <p className="text-xs text-content-muted">Combien traite-t-on lors de cette visite ?</p>
                                    {passageDossierActuel.lignes.map((l) => (
                                        <div key={cleLigneDossier(l)} className="flex items-center gap-2">
                                            <span className="flex-1 min-w-0 text-sm text-content truncate">{l.label} <span className="text-[11px] text-content-muted">(reste {l.quantity})</span></span>
                                            <input type="number" min={0} max={l.quantity} value={Math.max(0, Math.min(l.quantity, partsDossier[cleLigneDossier(l)] ?? l.quantity))} aria-label={`Quantité de ${l.label} pour cette visite`}
                                                onChange={(e) => { setDossierUtilise(''); setPartsDossier((prev) => ({ ...prev, [cleLigneDossier(l)]: Math.max(0, Math.min(l.quantity, Math.round(Number(e.target.value) || 0))) })); }}
                                                className="w-16 bg-surface border border-border rounded-lg px-2 py-1 text-sm text-content outline-none text-right" />
                                        </div>
                                    ))}
                                    <button type="button" onClick={utiliserDossier} className="px-3.5 py-2 rounded-xl text-xs font-bold bg-brand-600 hover:bg-brand-700 text-brand-fg">Utiliser pour ce rendez-vous</button>
                                    {dossierUtilise && <p className="text-xs font-semibold text-state-success">Rattaché à : {dossierUtilise}. La visite apparaîtra dans le suivi du dossier.</p>}
                                </>
                            ) : (
                                <p className="text-xs text-content-muted">Tout est déjà planifié sur ce dossier. Vous pouvez tout de même ajouter une prestation ci-dessous.</p>
                            )}
                        </div>
                    )}

                    <div className="space-y-2">
                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Prestations</label>
                        <select value="" onChange={(e) => { if (e.target.value) addPrestationItem(e.target.value); e.target.value = ''; }}
                            className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500">
                            <option value="">+ Ajouter une prestation...</option>
                            {/* Onglet Particulier : seulement ce qui est proposé aux particuliers (et à « Tous ») ; idem Professionnel. */}
                            {catalog.filter((item) => item.client_type_scope === 'TOUS' || item.client_type_scope === crmType).map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                        </select>
                        {prestationItems.map((item, idx) => (
                            <div key={idx} className="bg-surface-soft border border-border rounded-xl p-2 space-y-1.5">
                                <div className="flex items-center gap-2">
                                    <span className="flex-1 min-w-0 text-sm text-content truncate">{item.label}</span>
                                    <input type="number" min={0} step="1" value={item.quantity}
                                        onChange={(e) => updatePrestationItem(idx, { quantity: Number(e.target.value || 0) })}
                                        title="Quantité" className="w-14 bg-surface border border-border rounded-lg px-2 py-1 text-sm text-content outline-none text-right" />
                                    <button type="button" onClick={() => removePrestationItem(idx)} className="text-red-400 p-1 shrink-0"><X size={14} /></button>
                                </div>
                                {/* Remise en % ou en €, ou quantités offertes : elles figureront sur la facture. */}
                                <RemiseExtraField extra={item} onChange={(patch) => updatePrestationItem(idx, { ...patch, remise_auto: false })} />
                                {(item.remise_auto || item.offre_auto) && (
                                    <p className="text-[11px] font-semibold text-state-success">{item.offre_auto ? 'Offerte automatiquement (offre par quantité).' : 'Réduction appliquée automatiquement (tarifs et offres).'}</p>
                                )}
                            </div>
                        ))}
                        {prestationItems.length === 0 && <p className="text-xs text-content-muted italic">Aucune prestation sélectionnée.</p>}
                        {kmInfo && <p className="text-[11px] text-content-muted">{kmInfo}</p>}
                        {prestationItems.length > 0 && (() => {
                            const fmtEur = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);
                            const total = prestationItems.reduce((s, it) => s + totalLigneApresRemise(it), 0);
                            const remises = prestationItems.reduce((s, it) => s + ((Number(it.unit_price_ttc) || 0) * (Number(it.quantity) || 1) - totalLigneApresRemise(it)), 0);
                            return (
                                <div className="flex items-baseline justify-between gap-3 pt-1 text-sm">
                                    <span className="text-content-muted">{remises > 0.004 ? <>dont réductions et offres : <b className="text-state-success">−{fmtEur(remises)}</b></> : 'Total'}</span>
                                    <span className="font-bold text-content tabular-nums">{fmtEur(total)} TTC</span>
                                </div>
                            );
                        })()}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Date</label>
                            <input type="date" value={appointmentDate} onChange={(e) => setAppointmentDate(e.target.value)}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Heure de début</label>
                            <input type="time" value={startTime} min={minTime} max={maxTime} onChange={(e) => setStartTime(e.target.value)}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Statut paiement</label>
                            <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500">
                                {Object.entries(PAYMENT_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                        </div>
                    </div>
                    {!initial && regleReservation && regleReservation.payment_type !== 'none' && paymentStatus === 'a_encaisser' && (() => {
                        const total = prestationItems.reduce((sum, it: any) => sum + totalLigneApresRemise(it), 0);
                        const aPayer = regleReservation.payment_type === 'full' ? total : Math.round(total * regleReservation.payment_deposit_percent) / 100;
                        const montant = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(aPayer);
                        return (
                            <label className={`flex items-start gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-all ${demanderPaiement ? 'border-brand-600 bg-brand-500/5' : 'border-border bg-surface-soft'}`}>
                                <input type="checkbox" checked={demanderPaiement} onChange={(e) => setDemanderPaiement(e.target.checked)} className="mt-0.5 accent-brand-600 w-4 h-4" />
                                <span className="space-y-1">
                                    <span className="block text-sm font-bold text-content">Demander le paiement au client</span>
                                    <span className="block text-xs text-content-muted">
                                        {demanderPaiement
                                            ? `Le client reçoit par e-mail un lien pour régler ${aPayer > 0 ? montant : 'la somme due'}. Le rendez-vous est confirmé dès le paiement${regleReservation.payment_deadline_hours > 0 ? `, ou le créneau est libéré après ${regleReservation.payment_deadline_hours} h` : ''}.`
                                            : 'Le rendez-vous est confirmé tout de suite, sans lien de paiement.'}
                                    </span>
                                </span>
                            </label>
                        );
                    })()}
                    {!initial && (
                        <label className={`flex items-start gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-all ${dejaRealise ? 'border-brand-600 bg-brand-500/5' : 'border-border bg-surface-soft'}`}>
                            <input type="checkbox" checked={dejaRealise} onChange={(e) => setDejaRealise(e.target.checked)} className="mt-0.5 accent-brand-600 w-4 h-4" />
                            <span className="space-y-1">
                                <span className="block text-sm font-bold text-content">Prestation déjà réalisée (régularisation)</span>
                                <span className="block text-xs text-content-muted">
                                    {dejaRealise
                                        ? "Aucun e-mail de confirmation, aucun lien de paiement, les salariés ne sont pas prévenus. Ouvrez ensuite le rendez-vous et cliquez sur « Terminer la prestation » pour facturer."
                                        : "À cocher si l'intervention a déjà eu lieu hors de la plateforme et qu'il ne reste qu'à la facturer."}
                                </span>
                            </span>
                        </label>
                    )}
                    {dayHoursForModal.closed && (
                        <p className="text-xs text-warning-400 font-semibold">⚠️ Le {DAY_LABELS[dayKeyForDate(appointmentDate)].toLowerCase()} est marqué comme fermé dans les horaires — vérifiez avant de confirmer.</p>
                    )}

                    <div className="space-y-2">
                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5"><UsersIcon size={12} /> Employés assignés</label>
                        <div className="flex flex-wrap gap-2">
                            {employees.map((emp) => {
                                const active = employeeIds.includes(emp.id);
                                return (
                                    <button key={emp.id} type="button" onClick={() => toggleEmployee(emp.id)}
                                        className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${active ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                        {emp.first_name} {emp.last_name}
                                    </button>
                                );
                            })}
                            {employees.length === 0 && <p className="text-xs text-content-muted italic">Aucun employé configuré (Postes &amp; Employés).</p>}
                        </div>
                        {employeeIds.length > 1 && (
                            <div className="space-y-1 pt-1">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest" htmlFor="lead-employee">{roleLabel} sur place</label>
                                <select id="lead-employee" value={leadEmployeeId} onChange={(e) => setLeadEmployeeId(e.target.value)}
                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500">
                                    <option value="">Aucun : tous les salariés assignés peuvent commencer et terminer</option>
                                    {employees.filter((emp) => employeeIds.includes(emp.id)).map((emp) => (
                                        <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}{Number(emp.peut_etre_responsable) === 1 ? ` (${roleLabel.toLowerCase()})` : ''}</option>
                                    ))}
                                </select>
                                <p className="text-[11px] text-content-muted">Pour les entreprises, seule cette personne commence et termine la prestation (réglable dans Paramètres du Planning).</p>
                            </div>
                        )}
                    </div>

                    {employeeIds.length > 0 && totalPrestationDuration > 0 && (
                        <div className="flex items-center gap-2 p-3 rounded-xl bg-brand-500/10 border border-brand-500/20 text-xs text-content-muted">
                            <Clock size={14} className="text-brand-600 dark:text-brand-400 shrink-0" />
                            Durée calculée : <strong className="text-content">{formatDureeMinutes(computedDuration)}</strong> ({formatDureeMinutes(totalPrestationDuration)} de travail ÷ {employeeIds.length} employé{employeeIds.length > 1 ? 's' : ''})
                        </div>
                    )}
                    {/* Une prestation sans durée unitaire configurée casse silencieusement le calcul : le
                        rendez-vous naît avec une durée nulle et s'affiche « 09:00 - 09:00 » au planning.
                        On nomme les lignes fautives plutôt que d'inventer une durée par défaut. */}
                    {prestationsSansDuree.length > 0 && (
                        <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/25 text-xs text-content-muted">
                            <Clock size={14} className="text-amber-500 shrink-0 mt-0.5" />
                            <span>
                                <strong className="text-content">Durée non configurée</strong> pour {prestationsSansDuree.join(', ')}.
                                Renseignez la durée unitaire dans <strong className="text-content">Prestations</strong> (ex. 6 min par chaise) pour que la durée du rendez-vous se calcule toute seule.
                            </span>
                        </div>
                    )}

                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Notes</label>
                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                            className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500 resize-none" />
                    </div>

                    {!initial && (
                        <div className="space-y-2 p-3 rounded-xl bg-surface-soft border border-border">
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Récurrence (contrat régulier)</label>
                            <div className="grid grid-cols-4 gap-2">
                                {([
                                    { key: 'none', label: 'Aucune' },
                                    { key: 'weekly', label: 'Chaque semaine' },
                                    { key: 'biweekly', label: 'Toutes les 2 sem.' },
                                    { key: 'monthly', label: 'Chaque mois' },
                                ] as const).map((opt) => (
                                    <button key={opt.key} type="button" onClick={() => setRecurrenceFrequency(opt.key)}
                                        className={`px-2 py-2 rounded-lg text-[11px] font-bold border transition-all ${recurrenceFrequency === opt.key ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                            {recurrenceFrequency !== 'none' && (
                                <div className="flex items-center gap-2 pt-1">
                                    <label className="text-xs text-content-muted">Nombre d'occurrences à créer :</label>
                                    <input type="number" min={2} max={52} value={recurrenceOccurrences}
                                        onChange={(e) => setRecurrenceOccurrences(Math.min(52, Math.max(2, Number(e.target.value || 2))))}
                                        className="w-20 bg-surface border border-border rounded-lg px-2 py-1 text-sm text-content outline-none text-right" />
                                    <p className="text-[11px] text-content-muted italic">Une occurrence déjà en conflit avec un salarié assigné sera simplement ignorée.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {initial?.id && <LinkedNotesPanel kind="appointment" id={initial.id} />}

                    {error && <p className="text-xs text-red-400 font-semibold">{error}</p>}
                </div>
                <div className="flex justify-between items-center gap-3 p-6 border-t border-border shrink-0">
                    {initial && onDelete ? (
                        <button onClick={onDelete} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-red-400 hover:bg-red-500/10 text-sm font-bold transition-all">
                            <Trash2 size={14} /> Supprimer
                        </button>
                    ) : <span />}
                    <div className="flex gap-3">
                        <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-border text-content-muted hover:text-content font-bold text-sm">Annuler</button>
                        <button onClick={handleSubmit} disabled={saving}
                            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm disabled:opacity-50">
                            {saving ? 'Enregistrement...' : initial?.status === 'demande' ? 'Valider la demande' : initial ? 'Enregistrer' : 'Créer le rendez-vous'}
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}

/** Onglet « Client » de la fiche d'un rendez-vous (façon Zenchef) : coordonnées, puis les autres prestations de ce client
 *  avec leur état et leur paiement. Chargé seulement à l'ouverture de l'onglet. */
function ClientFicheTab({ appt }: { appt: Appointment }) {
    const [data, setData] = useState<any>(null);
    const [erreur, setErreur] = useState('');
    useEffect(() => {
        if (!appt.crm_contact_id) { setData({ prestations: [], totaux: { faites: 0, a_venir: 0, encaisse_ttc: 0 } }); return; }
        moduleApi.getPrestationContactHistory(appt.crm_contact_id, appt.id)
            .then((r: any) => setData(r))
            .catch(() => setErreur("L'historique n'a pas pu être chargé."));
    }, [appt.id]);
    const eur = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);
    const etat = (st: string) => st === 'termine' ? 'Terminée' : st === 'en_cours' ? 'En cours' : st === 'attente_paiement' ? 'En attente de paiement' : st === 'demande' ? 'À confirmer' : 'Planifiée';
    const nom = [appt.crm_first_name, appt.crm_last_name].filter(Boolean).join(' ') || appt.crm_company_name || 'Client';
    return (
        <div className="space-y-5">
            <div className="p-4 rounded-2xl bg-surface-soft border border-border space-y-1">
                <p className="text-sm font-bold text-content">{nom}</p>
                {appt.crm_phone && <p className="text-xs text-content-muted flex items-center gap-1.5"><Phone size={12} /> {appt.crm_phone}</p>}
                {appt.crm_email && <p className="text-xs text-content-muted flex items-center gap-1.5"><Mail size={12} /> {appt.crm_email}</p>}
                {(appt.crm_address || appt.crm_city) && (
                    <p className="text-xs text-content-muted flex items-center gap-1.5"><MapPin size={12} /> {[appt.crm_address, [appt.crm_postal_code, appt.crm_city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</p>
                )}
            </div>
            {erreur && <p className="text-xs text-red-400 font-semibold">{erreur}</p>}
            {!data && !erreur && <div className="flex justify-center py-6"><Loader2 className="animate-spin text-content-muted" size={18} /></div>}
            {data && (
                <>
                    <div className="grid grid-cols-3 gap-2">
                        {[{ n: data.totaux.faites, l: 'prestations faites' }, { n: data.totaux.a_venir, l: 'à venir' }, { n: eur(data.totaux.encaisse_ttc), l: 'déjà payé' }].map((m) => (
                            <div key={m.l} className="p-3 rounded-xl bg-surface-soft border border-border text-center">
                                <p className="text-base font-bold text-content tabular-nums">{m.n}</p>
                                <p className="text-[10px] text-content-muted">{m.l}</p>
                            </div>
                        ))}
                    </div>
                    <div className="space-y-2">
                        <h4 className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Autres prestations de ce client</h4>
                        {data.prestations.length === 0 ? (
                            <p className="text-xs text-content-muted italic">C'est sa première prestation chez vous.</p>
                        ) : data.prestations.map((p: any) => (
                            <div key={p.id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border bg-surface">
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold text-content truncate">{p.label}</p>
                                    <p className="text-[11px] text-content-muted">{new Date(p.date + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })} · {etat(p.status)}</p>
                                </div>
                                <div className="text-right shrink-0">
                                    <p className="text-sm font-bold text-content tabular-nums">{eur(p.total_ttc)}</p>
                                    <p className={`text-[10px] font-black uppercase ${p.payment_status === 'encaisse' ? 'text-green-600' : 'text-content-muted'}`}>{p.payment_status === 'encaisse' ? 'Payé' : 'À encaisser'}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function AppointmentDetailModal({
    appt, employees, catalog, onClose, onEdit, onDelete, onRefresh,
}: {
    appt: Appointment;
    employees: Employee[];
    catalog: PrestationCatalogItem[];
    onClose: () => void;
    onEdit: () => void;
    onDelete: () => Promise<void>;
    onRefresh: () => Promise<void>;
}) {
    const [emails, setEmails] = useState<EmailLogEntry[]>([]);
    const [loadingEmails, setLoadingEmails] = useState(true);
    const [previewEmail, setPreviewEmail] = useState<EmailLogEntry | null>(null);
    const [resendingEmailId, setResendingEmailId] = useState<string | null>(null);
    const [resendEmailResult, setResendEmailResult] = useState<{ id: string; ok: boolean; msg: string } | null>(null);
    const [sendingReminder, setSendingReminder] = useState(false);
    // Quel rappel est en cours : seul le bouton cliqué affiche « Envoi… » (les deux partagent `sendingReminder`).
    const [rappelEnCours, setRappelEnCours] = useState<'employees' | 'client' | null>(null);
    // Remboursement d'un paiement en ligne : montant proposé = tout ce qui a été payé par carte et pas encore remboursé.
    const [refundAmount, setRefundAmount] = useState('');
    const [refundBusy, setRefundBusy] = useState(false);
    const [refundMsg, setRefundMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const payeEnLigne = (appt.payment_breakdown || []).filter((e) => e.method === 'Carte en ligne (Stripe)').reduce((s, e) => s + Number(e.amount || 0), 0);
    const dejaRembourse = Math.abs((appt.payment_breakdown || []).filter((e) => e.method === 'Remboursement (Stripe)').reduce((s, e) => s + Number(e.amount || 0), 0));
    const remboursable = appt.payment_intent_id ? Math.max(0, Math.round((payeEnLigne - dejaRembourse) * 100) / 100) : 0;
    const rembourser = async () => {
        const montant = Number(String(refundAmount || remboursable).replace(',', '.'));
        if (!(montant > 0) || montant > remboursable + 0.005) { setRefundMsg({ type: 'error', text: `Montant invalide (maximum ${fmt(remboursable)}).` }); return; }
        if (!window.confirm(`Rembourser ${fmt(montant)} au client par carte ? Une facture d'avoir lui sera envoyée par e-mail. Cette action est définitive.`)) return;
        setRefundBusy(true); setRefundMsg(null);
        try {
            const res: any = await moduleApi.refundPrestationAppointment(appt.id, montant);
            if (res?.error) { setRefundMsg({ type: 'error', text: res.error }); return; }
            let texte = `${fmt(montant)} remboursés. Le client les reçoit sous quelques jours.`;
            // Facture d'avoir : fabriquée ici (comme les factures) puis envoyée au client.
            if (res?.credit_note) {
                try {
                    const { generateInvoicePdfFromData } = await import('../Factures/invoicePdfGenerator');
                    const { pdf } = await generateInvoicePdfFromData({ invoice: res.credit_note.invoice, payload: res.credit_note.payload, snapshot: res.credit_note.snapshot });
                    const pdfBase64 = String(pdf.output('datauristring')).split(',')[1];
                    if (res.recipient_email) {
                        const envoi: any = await moduleApi.sendPrestationCreditNote(appt.id, { credit_note_id: res.credit_note.id, pdfBase64, filename: `${res.credit_note.invoice_number}.pdf` });
                        texte += envoi?.error ? ` Avoir ${res.credit_note.invoice_number} créé, mais non envoyé : ${envoi.error}` : ` Avoir ${res.credit_note.invoice_number} envoyé au client.`;
                    } else texte += ` Avoir ${res.credit_note.invoice_number} créé (le client n'a pas d'adresse e-mail).`;
                } catch (e: any) { texte += ` Avoir ${res.credit_note.invoice_number} créé, mais son PDF n'a pas pu être envoyé.`; }
            }
            setRefundMsg({ type: 'success', text: texte });
            setRefundAmount('');
            await onRefresh();
        } catch (e: any) { setRefundMsg({ type: 'error', text: e?.message || 'Le remboursement a échoué.' }); }
        finally { setRefundBusy(false); }
    };
    const [onglet, setOnglet] = useState<'rdv' | 'client' | 'emails'>('rdv');
    const [resendingPaymentLink, setResendingPaymentLink] = useState(false);
    // Envois déclenchés d'un clic (lien de paiement du solde, demande d'avis).
    const [envoiEnCours, setEnvoiEnCours] = useState<'' | 'paiement'>('');
    const [envoiMessage, setEnvoiMessage] = useState<{ ok: boolean; text: string } | null>(null);
    // Pas de bouton « Demander un avis » : l'avis n'a pas d'e-mail à lui, c'est un bouton que l'établissement place dans le
    // mail de fin de prestation ou de règlement (jeton {{lien_avis}}). Seul le lien de paiement part d'un clic.
    const declencherEnvoi = async (quoi: 'paiement') => {
        setEnvoiEnCours(quoi); setEnvoiMessage(null);
        try {
            const res: any = await moduleApi.sendPrestationPaymentLink(appt.id);
            if (res?.error) setEnvoiMessage({ ok: false, text: res.error });
            else setEnvoiMessage({ ok: true, text: `Lien de paiement envoyé à ${res.recipient}.` });
        } catch (e: any) { setEnvoiMessage({ ok: false, text: e?.message || "L'envoi a échoué." }); }
        finally { setEnvoiEnCours(''); }
    };
    const [paymentLinkMessage, setPaymentLinkMessage] = useState<{ ok: boolean; text: string } | null>(null);
    const renvoyerLienPaiement = async () => {
        setResendingPaymentLink(true); setPaymentLinkMessage(null);
        try {
            const res: any = await moduleApi.resendPrestationPaymentLink(appt.id);
            setPaymentLinkMessage({ ok: true, text: `Lien renvoyé à ${res?.recipient || 'votre client'}.` });
        } catch (e: any) {
            setPaymentLinkMessage({ ok: false, text: e?.message || "Le lien n'a pas pu être renvoyé." });
        } finally {
            setResendingPaymentLink(false);
        }
    };
    const [reminderMessage, setReminderMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    // Lien de paiement envoyé à une autre adresse que celle de la fiche client (laissé vide : adresse de la fiche).
    const [autreAdresseLien, setAutreAdresseLien] = useState('');
    const [showMarkPaid, setShowMarkPaid] = useState(false);
    const [showSendMessage, setShowSendMessage] = useState(false);
    const [showCompletion, setShowCompletion] = useState(false);
    const [showStart, setShowStart] = useState(false);
    const [downloadingCompletion, setDownloadingCompletion] = useState(false);
    const [completionDownloadError, setCompletionDownloadError] = useState('');

    // Régénère le PDF du constat de clôture à la volée depuis les données stockées (photos/signatures) —
    // jamais de PDF pré-généré conservé, cf. le même choix pour la facture. Échoue proprement (message
    // explicite) sur un rendez-vous clôturé avant l'ajout de cette fonctionnalité, qui n'a donc pas ces
    // données.
    const handleDownloadCompletionDocument = async () => {
        setDownloadingCompletion(true);
        setCompletionDownloadError('');
        try {
            const docData: any = await moduleApi.getPrestationCompletionDocumentData(appt.id);
            if (!docData || docData.error) { setCompletionDownloadError(docData?.error || 'Constat introuvable pour ce rendez-vous.'); return; }
            const { downloadPrestationCompletionPdf } = await import('../../lib/prestationCompletionPdfGenerator');
            await downloadPrestationCompletionPdf({
                appointmentId: docData.appointment_id,
                companyName: docData.company_name,
                companyLogoUrl: docData.company_logo_url,
                legal: docData.legal,
                clientName: docData.client_name,
                prestationLabel: docData.prestation_label,
                appointmentDate: docData.appointment_date,
                employeeNames: docData.employee_names,
                photoBeforeUrl: docData.photo_before_url,
                photoAfterUrl: docData.photo_after_url,
                photosEnsemble: docData.photos_ensemble,
                comment: docData.comment,
                clientSignatureUrl: docData.client_signature_url,
                clientSignatureName: docData.client_signature_name,
                establishmentSignatureUrl: docData.establishment_signature_url,
            });
        } catch {
            setCompletionDownloadError('Erreur lors de la génération du document.');
        } finally {
            setDownloadingCompletion(false);
        }
    };
    const [markPaidEntries, setMarkPaidEntries] = useState<{ method: string; amount: number }[]>([{ method: '', amount: 0 }]);
    const [markingPaid, setMarkingPaid] = useState(false);
    const [markPaidError, setMarkPaidError] = useState('');
    const [showAssign, setShowAssign] = useState(false);
    const [assigningIds, setAssigningIds] = useState<string[]>(appt.employee_ids || []);
    const [assigning, setAssigning] = useState(false);
    const [assignError, setAssignError] = useState('');

    const toggleAssignId = (id: string) => setAssigningIds((prev) => prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]);

    // Assignation rapide, sans passer par "Modifier" (qui rouvre tout le formulaire) — renvoie le même
    // payload complet attendu par PUT /prestation-planning/appointments/:id (reconstruit à partir de ce
    // rendez-vous), avec les employés sélectionnés ici comme seule différence.
    const handleQuickAssign = async () => {
        if (assigningIds.length === 0) { setAssignError('Sélectionnez au moins un employé.'); return; }
        setAssigning(true);
        setAssignError('');
        try {
            const res: any = await moduleApi.updatePrestationAppointment(appt.id, {
                crm_contact_id: appt.crm_contact_id || undefined,
                crm_type: appt.crm_company_name ? 'PROFESSIONNEL' : 'PRIVÉ',
                first_name: appt.crm_first_name || '',
                last_name: appt.crm_last_name || '',
                company_name: appt.crm_company_name || '',
                phone: appt.crm_phone || '',
                email: appt.crm_email || '',
                address: appt.crm_address || '',
                postal_code: appt.crm_postal_code || '',
                city: appt.crm_city || '',
                employee_ids: assigningIds,
                catalog_items: appt.catalog_items && appt.catalog_items.length > 0 ? appt.catalog_items : [{
                    catalog_item_id: appt.prestation_catalog_item_id, label: appt.prestation_label, quantity: appt.quantity,
                    unit_price_ttc: appt.unit_price_ttc, tax_rate: appt.tax_rate, unit_duration_minutes: appt.unit_duration_minutes,
                }],
                appointment_date: appt.appointment_date,
                start_time: appt.start_time,
                payment_status: appt.payment_status,
                payment_method: appt.payment_method,
                notes: appt.notes,
                location_type: appt.location_type,
            });
            if (res?.error) { setAssignError(res.error); return; }
            setShowAssign(false);
            setAssigningIds([]);
            await onRefresh();
            onClose();
        } catch (e) {
            setAssignError('Erreur lors de l\'assignation.');
        } finally {
            setAssigning(false);
        }
    };

    const loadEmails = () => {
        moduleApi.getPrestationAppointmentEmails(appt.id)
            .then((d: any) => setEmails(Array.isArray(d) ? d : []))
            .catch(() => setEmails([]))
            .finally(() => setLoadingEmails(false));
    };

    useEffect(() => { loadEmails(); }, [appt.id]);

    // Renvoie exactement l'email choisi dans l'historique (même sujet, même contenu déjà envoyé) — pas besoin
    // de savoir quel type c'est ni de recalculer son contenu, contrairement aux boutons "Renvoyer" dédiés
    // (confirmation/rappel/facture) qui existent par ailleurs.
    const handleResendEmail = async (emailId: string) => {
        setResendingEmailId(emailId);
        setResendEmailResult(null);
        try {
            // Un mail de FACTURE n'est pas renvoyé tel quel : sa copie enregistrée n'a ni la facture PDF en pièce jointe, ni les
            // boutons créés depuis (avis…). On régénère la facture et on renvoie le mail avec le modèle actuel de
            // « Merci pour votre règlement » — seulement si la prestation est soldée (sinon aucune facture ne part).
            const entree = emails.find((x) => x.id === emailId);
            if (entree?.email_type === 'invoice_completion' && (appt as any).facture_id && appt.crm_email) {
                const factures: any[] = await moduleApi.getFactures();
                const invoice = (Array.isArray(factures) ? factures : []).find((f: any) => String(f.id) === String((appt as any).facture_id));
                if (!invoice) { setResendEmailResult({ id: emailId, ok: false, msg: 'Facture introuvable.' }); return; }
                const payload = invoice.payload_json || {};
                let snapshot = invoice.billing_snapshot || {};
                if (!snapshot?.company_name) {
                    try {
                        const fresh = await moduleApi.getBillingSettings();
                        if (fresh?.company_name) snapshot = { ...fresh, ...snapshot };
                    } catch { /* ignore */ }
                }
                const { generateInvoicePdfFromData } = await import('../Factures/invoicePdfGenerator');
                const { pdf } = await generateInvoicePdfFromData({ invoice, payload, snapshot });
                const pdfBase64 = (pdf.output('datauristring') as string).split(',')[1];
                const envoi: any = await moduleApi.sendPrestationInvoiceEmail(appt.id, { to: appt.crm_email, pdfBase64, filename: `${invoice.invoice_number || 'facture'}.pdf` });
                if (envoi?.success) {
                    setResendEmailResult({ id: emailId, ok: true, msg: 'Envoyé avec la facture.' });
                    loadEmails();
                } else {
                    setResendEmailResult({ id: emailId, ok: false, msg: envoi?.error || "Rien n'a été envoyé : la prestation n'est pas encore soldée." });
                }
                return;
            }
            const res: any = await moduleApi.resendPrestationAppointmentEmail(appt.id, emailId);
            if (res?.success) {
                setResendEmailResult({ id: emailId, ok: true, msg: 'Envoyé.' });
                loadEmails();
            } else {
                setResendEmailResult({ id: emailId, ok: false, msg: res?.error || 'Erreur lors de l\'envoi.' });
            }
        } catch {
            setResendEmailResult({ id: emailId, ok: false, msg: 'Erreur lors de l\'envoi.' });
        } finally {
            setResendingEmailId(null);
        }
    };

    // « Renvoyer le dernier mail » : le dernier e-mail envoyé au client pour ce rendez-vous (mêmes règles que « Renvoyer »
    // dans l'historique des e-mails : un mail de facture est régénéré, les autres repartent tels quels).
    const renvoyerDernierMail = async () => {
        if (emails[0]) await handleResendEmail(emails[0].id);
    };

    const handleSendReminder = async (audience?: 'employees' | 'client') => {
        setRappelEnCours(audience || null);
        setSendingReminder(true);
        setReminderMessage(null);
        try {
            const res: any = await moduleApi.sendPrestationReminder(appt.id, audience);
            if (res?.success) {
                // Dit précisément qui a reçu quoi (le serveur renvoie le nombre de salariés et si le client a reçu le sien).
                const nbSalaries = Number(res?.employee_sent) || 0;
                const recus = [
                    nbSalaries > 0 ? `${nbSalaries} salarié${nbSalaries > 1 ? 's' : ''}` : '',
                    res?.client_sent ? 'le client' : '',
                ].filter(Boolean).join(' et ');
                setReminderMessage({ type: 'success', text: `${recus ? `Rappel envoyé à ${recus}.` : 'Rappel envoyé.'}${res?.warning ? ` ${res.warning}` : ''}` });
                loadEmails();
            } else {
                setReminderMessage({ type: 'error', text: res?.error || 'Erreur lors de l\'envoi.' });
            }
        } catch {
            setReminderMessage({ type: 'error', text: 'Erreur lors de l\'envoi.' });
        } finally {
            setSendingReminder(false);
            setRappelEnCours(null);
        }
    };

    // "Relancer pour le paiement" — envoie au client son lien de paiement pour le solde restant, SANS la facture :
    // la facture ne part qu'une fois la prestation payée en intégralité (décision d'Emeni, 2026-10-19).
    const handleSendPaymentReminder = async () => {
        const autre = autreAdresseLien.trim();
        if (!appt.crm_email && !autre) {
            setReminderMessage({ type: 'error', text: 'Aucune adresse email renseignée pour ce client.' });
            return;
        }
        setSendingReminder(true);
        setReminderMessage(null);
        try {
            const res: any = await moduleApi.sendPrestationPaymentLink(appt.id, autre || undefined);
            if (res?.success) {
                setReminderMessage({ type: 'success', text: `Lien de paiement envoyé${res?.recipient ? ` à ${res.recipient}` : ''}.` });
                loadEmails();
            } else {
                setReminderMessage({ type: 'error', text: res?.error || 'Erreur lors de l\'envoi.' });
            }
        } catch (e: any) {
            setReminderMessage({ type: 'error', text: e?.message || 'Erreur lors de l\'envoi.' });
        } finally {
            setSendingReminder(false);
        }
    };

    const endTime = minutesToTime(timeToMinutes(appt.start_time) + Math.round(appt.computed_duration_minutes));
    const fullAddress = [appt.crm_address, [appt.crm_postal_code, appt.crm_city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    const mapsUrl = fullAddress ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}` : null;
    const remainingDue = Math.max(0, Number(appt.total_amount_ttc || 0) - Number(appt.amount_already_paid || 0));
    const isPendingRequest = appt.status === 'demande';
    const isClosed = appt.status === 'termine';
    // Ce qu'il reste à régler : de quoi proposer « Envoyer le lien de paiement ».
    const soldeDu = Math.max(0, (isClosed ? Number(appt.total_amount_ttc || 0) : Number(appt.quantity || 1) * Number(appt.unit_price_ttc || 0)) - Number(appt.amount_already_paid || 0));
    // Une fois clôturée ET payée intégralement, plus rien à notifier ni à modifier — la "date passée"
    // n'est plus le critère : une prestation clôturée mais pas encore payée en intégralité doit au
    // contraire pouvoir être relancée pour le paiement, peu importe la date.
    const isFullyPaid = isClosed && Number(appt.total_amount_ttc || 0) > 0 && remainingDue <= 0;
    const isClosedUnpaid = isClosed && remainingDue > 0;
    const fmt = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

    const markPaidEntriesSum = markPaidEntries.reduce((s, e) => s + Number(e.amount || 0), 0);
    const markPaidEntriesValid = markPaidEntries.every((e) => e.method && Number(e.amount) > 0) && Math.abs(markPaidEntriesSum - remainingDue) < 0.01;
    const updateMarkPaidEntry = (idx: number, patch: Partial<{ method: string; amount: number }>) =>
        setMarkPaidEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
    const addMarkPaidEntry = () => {
        const allocated = markPaidEntries.reduce((s, e) => s + Number(e.amount || 0), 0);
        setMarkPaidEntries((prev) => [...prev, { method: '', amount: Math.max(0, Math.round((remainingDue - allocated) * 100) / 100) }]);
    };
    const removeMarkPaidEntry = (idx: number) => setMarkPaidEntries((prev) => prev.filter((_, i) => i !== idx));

    const handleMarkPaid = async () => {
        if (!markPaidEntriesValid) return;
        setMarkingPaid(true);
        setMarkPaidError('');
        try {
            const res: any = await moduleApi.markPrestationAppointmentPaid(appt.id, markPaidEntries);
            if (res?.error) { setMarkPaidError(res.error); setMarkingPaid(false); return; }
            // L'email "prestation soldée" part quoi qu'il arrive — la facture PDF n'est jointe que si le
            // module Facture est actif (res.invoice alors renseigné).
            if (res?.recipient_email) {
                try {
                    let pdfBase64: string | undefined, filename: string | undefined;
                    if (res.invoice) {
                        const { generateInvoicePdfFromData } = await import('../Factures/invoicePdfGenerator');
                        const { pdf } = await generateInvoicePdfFromData({ invoice: res.invoice, payload: res.payload, snapshot: res.snapshot });
                        const dataUri: string = pdf.output('datauristring');
                        pdfBase64 = dataUri.split(',')[1];
                        filename = `${res.invoice?.invoice_number || 'facture'}.pdf`;
                    }
                    await moduleApi.sendPrestationInvoiceEmail(appt.id, { to: res.recipient_email, pdfBase64, filename });
                } catch (pdfErr) {
                    console.error('Failed to generate/send invoice email', pdfErr);
                }
            }
            setShowMarkPaid(false);
            await onRefresh();
            onClose();
        } catch (e) {
            setMarkPaidError('Erreur lors de la mise à jour du paiement.');
        } finally {
            setMarkingPaid(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-lg bg-surface border border-border rounded-3xl max-h-[90vh] shadow-2xl flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b border-border shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-11 h-11 rounded-2xl bg-brand-500/15 flex items-center justify-center text-brand-500 shrink-0">
                            <User size={20} />
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-lg font-bold text-content truncate">{contactLabel(appt)}</h3>
                            {appt.commercial_nom && (
                                <p className="text-xs font-semibold text-content-muted truncate">Pris par {appt.commercial_nom}</p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {isPendingRequest ? (
                            <span className="text-[10px] font-black uppercase bg-warning-500/10 text-warning-500 border border-warning-500/20 px-2 py-1 rounded-lg">Demande à confirmer</span>
                        ) : appt.status === 'attente_paiement' ? (
                            <span className="text-[10px] font-black uppercase bg-slate-500/10 text-slate-400 border border-slate-500/20 px-2 py-1 rounded-lg">En attente de paiement</span>
                        ) : appt.status === 'termine' ? (
                            <span className="text-[10px] font-black uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-1 rounded-lg">Terminé</span>
                        ) : appt.status === 'en_cours' ? (
                            <span className="text-[10px] font-black uppercase bg-brand-500/10 text-brand-500 border border-brand-500/20 px-2 py-1 rounded-lg">En cours</span>
                        ) : (
                            <span className="text-[10px] font-black uppercase bg-warning-500/10 text-warning-400 border border-warning-500/20 px-2 py-1 rounded-lg">Planifié</span>
                        )}
                        <button onClick={onClose} className="p-2 text-content-muted hover:text-content rounded-xl hover:bg-surface-soft"><X size={20} /></button>
                    </div>
                </div>
                {isPendingRequest && (
                    <div className="mx-6 mt-4 p-3 rounded-xl bg-warning-500/10 border border-warning-500/20 text-xs text-warning-500 font-semibold">
                        Demande reçue en ligne. Choisissez le ou les salariés dans « Équipe sur cette mission » ci-dessous, puis confirmez : le client est prévenu aussitôt.
                    </div>
                )}
                {appt.status === 'attente_paiement' && (
                    <div className="mx-6 mt-4 p-4 rounded-2xl bg-warning-500/10 border border-warning-500/30 space-y-3">
                        <div className="space-y-1">
                            <p className="text-sm font-bold text-content">En attente de paiement</p>
                            <p className="text-xs text-content-muted">
                                Le créneau est retenu, mais pas encore confirmé : le client n'a pas payé{Number(appt.payment_gate_amount_due) > 0 ? ` les ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(appt.payment_gate_amount_due))} demandés` : ''}. Il sera confirmé automatiquement dès le paiement.
                            </p>
                        </div>
                        <button type="button" onClick={renvoyerLienPaiement} disabled={resendingPaymentLink}
                            className="px-4 py-2 rounded-xl text-xs font-bold bg-brand-600 text-brand-fg disabled:opacity-60">
                            {resendingPaymentLink ? 'Envoi…' : 'Renvoyer le lien de paiement'}
                        </button>
                        {paymentLinkMessage && <p className={`text-xs font-semibold ${paymentLinkMessage.ok ? 'text-state-success' : 'text-state-error'}`}>{paymentLinkMessage.text}</p>}
                    </div>
                )}
                <div role="tablist" aria-label="Fiche du rendez-vous" className="flex gap-1 px-6 pt-3 border-b border-border shrink-0">
                    {([['rdv', 'Rendez-vous'], ['client', 'Client'], ['emails', 'E-mails']] as const).map(([cle, libelle]) => (
                        <button key={cle} type="button" role="tab" aria-selected={onglet === cle} onClick={() => setOnglet(cle)}
                            className={`px-4 py-2 text-xs font-bold border-b-2 -mb-px transition-colors ${onglet === cle ? 'border-brand-600 text-content' : 'border-transparent text-content-muted hover:text-content'}`}>
                            {libelle}
                        </button>
                    ))}
                </div>
                <div className="p-6 overflow-y-auto">
                    <div className={onglet === 'rdv' ? 'space-y-5' : 'hidden'}>
                    {!isPendingRequest && appt.status !== 'attente_paiement' && (soldeDu > 0 || isClosed || emails.length > 0) && (
                        <div className="p-4 rounded-2xl bg-surface-soft border border-border space-y-2.5">
                            <p className="text-[10px] font-black uppercase tracking-widest text-content-muted">Envoyer au client</p>
                            <div className="flex flex-wrap gap-2">
                                {soldeDu > 0 && (
                                    <button type="button" onClick={() => declencherEnvoi('paiement')} disabled={envoiEnCours !== ''}
                                        className="px-3.5 py-2 rounded-xl text-xs font-bold border border-brand-500 text-brand-600 dark:text-brand-400 hover:bg-brand-500/10 disabled:opacity-60">
                                        {envoiEnCours === 'paiement' ? 'Envoi…' : 'Envoyer le lien de paiement'}
                                    </button>
                                )}
                                {/* « Renvoyer le dernier mail » : l'avis n'a pas de mail à lui, il est demandé dans le mail de clôture ou de
                                    facture. Ce bouton renvoie donc, tel quel, le dernier e-mail envoyé au client pour ce rendez-vous. */}
                                {emails.length > 0 && (
                                    <button type="button" onClick={renvoyerDernierMail} disabled={resendingEmailId !== null}
                                        className="px-3.5 py-2 rounded-xl text-xs font-bold border border-brand-500 text-brand-600 dark:text-brand-400 hover:bg-brand-500/10 disabled:opacity-60">
                                        {resendingEmailId === emails[0].id ? 'Envoi…' : 'Renvoyer le dernier mail'}
                                    </button>
                                )}
                            </div>
                            {emails.length > 0 && (
                                <p className="text-[11px] text-content-muted">
                                    Dernier mail : « {emails[0].subject} » — envoyé le {new Date(emails[0].sent_at).toLocaleDateString('fr-FR')} à {emails[0].recipient_email}.
                                    {emails[0].email_type === 'invoice_completion' && ' Pour une facture, le mail est régénéré avec la facture PDF et votre modèle actuel.'}
                                    {resendEmailResult?.id === emails[0].id && <span className={`ml-1 font-semibold ${resendEmailResult.ok ? 'text-state-success' : 'text-state-error'}`}>{resendEmailResult.msg}</span>}
                                </p>
                            )}
                            {(appt as any).payment_link_sent_at && (
                                <p className="text-[11px] text-content-muted">
                                    {`Lien de paiement envoyé le ${new Date((appt as any).payment_link_sent_at).toLocaleDateString('fr-FR')}.`}
                                </p>
                            )}
                            {envoiMessage && <p className={`text-xs font-semibold ${envoiMessage.ok ? 'text-state-success' : 'text-state-error'}`}>{envoiMessage.text}</p>}
                        </div>
                    )}
                    <div className="p-4 rounded-2xl bg-surface-soft border border-border">
                        <div className="flex items-center justify-between text-content">
                            <span className="text-xs text-content-muted">Début</span>
                            <span className="font-bold text-sm">{appt.start_time}</span>
                        </div>
                        <div className="flex items-center justify-between text-content mt-2">
                            <span className="text-xs text-content-muted">Fin (approximative)</span>
                            <span className="font-bold text-sm">{endTime}</span>
                        </div>
                        <div className="mt-3 pt-3 border-t border-border text-[10px] text-content-muted font-bold uppercase tracking-wider capitalize">
                            {formatDateHuman(appt.appointment_date)}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <h4 className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Prestations</h4>
                        <div className="space-y-1">
                            {(appt.catalog_items && appt.catalog_items.length > 0 ? appt.catalog_items : [{ label: appt.prestation_label, quantity: appt.quantity, unit_price_ttc: appt.unit_price_ttc, catalog_item_id: null, tax_rate: 0, unit_duration_minutes: 0 }]).map((item, idx) => (
                                <p key={idx} className="text-sm text-content">
                                    {item.label}{item.quantity > 1 ? ` × ${item.quantity}` : ''}
                                </p>
                            ))}
                            {!!appt.km_fee_amount && appt.km_fee_amount > 0 && (
                                <p className="text-sm text-content flex items-center justify-between">
                                    <span>Frais kilométriques (réglés en ligne)</span>
                                    <span className="font-bold">{fmt(appt.km_fee_amount)}</span>
                                </p>
                            )}
                        </div>
                    </div>

                    {appt.status === 'termine' && (
                        <div className="space-y-2">
                            <h4 className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Paiement</h4>
                            <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-1.5 text-sm">
                                {(appt.payment_breakdown && appt.payment_breakdown.length > 0
                                    ? appt.payment_breakdown
                                    : (appt.payment_method ? [{ method: appt.payment_method, amount: Number(appt.amount_already_paid || 0) }] : [])
                                ).map((entry, idx) => (
                                    <div key={idx} className="flex items-center justify-between">
                                        <span className="text-content-muted">{entry.method}</span>
                                        <span className="font-bold text-content">{fmt(entry.amount)}</span>
                                    </div>
                                ))}
                                {!appt.payment_method && (!appt.payment_breakdown || appt.payment_breakdown.length === 0) && (
                                    <p className="text-content-muted italic text-xs">Aucun règlement enregistré.</p>
                                )}
                                <div className="pt-1.5 border-t border-border flex items-center justify-between">
                                    <span className="text-content-muted">{isFullyPaid ? 'Total réglé' : 'Réglé à ce jour'}</span>
                                    <span className={`font-black ${isFullyPaid ? 'text-emerald-400' : 'text-content'}`}>{fmt(Number(appt.amount_already_paid || 0))}</span>
                                </div>
                                {remboursable > 0 && (
                                    <div className="pt-2 border-t border-border space-y-2">
                                        <p className="text-[11px] text-content-muted">Payé en ligne : {fmt(remboursable)} remboursable. L'argent retourne sur la carte du client sous quelques jours.</p>
                                        <div className="flex items-center gap-2">
                                            <input type="number" step="0.01" min="0.01" max={remboursable} placeholder={String(remboursable)} value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)}
                                                className="w-28 bg-surface border border-border rounded-lg px-2.5 py-1.5 text-sm text-content outline-none focus:border-brand-500" aria-label="Montant à rembourser" />
                                            <button type="button" onClick={rembourser} disabled={refundBusy}
                                                className="px-3 py-1.5 rounded-lg border border-red-500/40 text-red-500 text-xs font-bold hover:bg-red-500/10 disabled:opacity-50">{refundBusy ? 'Remboursement…' : 'Rembourser'}</button>
                                        </div>
                                        <p className="text-[11px] text-content-muted">La facture reste inchangée : une facture d'avoir est établie et envoyée au client.</p>
                                    </div>
                                )}
                                {refundMsg && <p className={`text-xs font-semibold ${refundMsg.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>{refundMsg.text}</p>}
                                {phraseTempsPasse(appt.started_at, appt.completed_at, appt.computed_duration_minutes) && (
                                    <p className="text-[11px] text-content-muted pt-0.5">{phraseTempsPasse(appt.started_at, appt.completed_at, appt.computed_duration_minutes)}</p>
                                )}
                                {appt.completed_at && (
                                    <p className="text-[11px] text-content-muted pt-0.5">
                                        Clôturé le {new Date(appt.completed_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {appt.status === 'termine' && (appt.completion_report || []).some((l) => l.quantity_done < l.quantity_planned) && (
                        <div className="space-y-2">
                            <h4 className="text-[10px] font-bold text-amber-500 uppercase tracking-widest">Pas tout fait — pense à planifier un rattrapage</h4>
                            <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/20 space-y-1.5 text-sm">
                                {(appt.completion_report || []).filter((l) => l.quantity_done < l.quantity_planned).map((l, i) => (
                                    <div key={i} className="flex items-center justify-between">
                                        <span className="text-content">{l.label}</span>
                                        <span className="font-bold text-amber-500">{l.quantity_done} / {l.quantity_planned} fait{l.quantity_planned > 1 ? 's' : ''}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {appt.status === 'termine' && (
                        <button onClick={handleDownloadCompletionDocument} disabled={downloadingCompletion}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-xs font-bold transition-all disabled:opacity-50">
                            <FileText size={14} /> {downloadingCompletion ? 'Génération...' : 'Télécharger le constat de prestation'}
                        </button>
                    )}
                    {completionDownloadError && <p className="text-xs text-red-400 font-semibold">{completionDownloadError}</p>}

                    {appt.status === 'termine' && appt.payment_status !== 'encaisse' && remainingDue > 0 && (
                        <div className="p-4 rounded-2xl bg-red-500/5 border border-red-500/20 space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-content-muted uppercase tracking-widest">Reste à payer</span>
                                <span className="font-black text-red-400">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(remainingDue)}</span>
                            </div>
                            {!showMarkPaid ? (
                                <button onClick={() => { setMarkPaidEntries([{ method: '', amount: remainingDue }]); setShowMarkPaid(true); }} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-all">
                                    <CheckCircle size={14} /> Marquer comme payé
                                </button>
                            ) : (
                                <div className="space-y-2">
                                    <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Moyen(s) de paiement utilisé(s)</p>
                                    {markPaidEntries.map((entry, idx) => (
                                        <div key={idx} className="space-y-1.5 p-2 rounded-xl bg-surface border border-border">
                                            <div className="flex gap-2">
                                                {['Espèces', 'Virement', 'Carte bleue'].map((m) => (
                                                    <button key={m} onClick={() => updateMarkPaidEntry(idx, { method: m })}
                                                        className={`flex-1 px-2 py-1.5 rounded-lg text-[11px] font-bold border transition-all ${entry.method === m ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-border text-content-muted hover:text-content'}`}>
                                                        {m}
                                                    </button>
                                                ))}
                                            </div>
                                            {markPaidEntries.length > 1 && (
                                                <div className="flex items-center gap-2">
                                                    <input type="number" min={0} step="0.01" value={entry.amount}
                                                        onChange={(e) => updateMarkPaidEntry(idx, { amount: Number(e.target.value || 0) })}
                                                        placeholder="Montant" title="Montant pour ce moyen de paiement"
                                                        className="flex-1 bg-surface-soft border border-border rounded-lg px-2 py-1.5 text-sm text-content outline-none focus:border-brand-500" />
                                                    <button type="button" onClick={() => removeMarkPaidEntry(idx)} className="text-red-400 p-1 shrink-0"><Trash2 size={14} /></button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    <button type="button" onClick={addMarkPaidEntry} className="text-xs font-bold text-brand-600 dark:text-brand-400 flex items-center gap-1.5">
                                        <Plus size={14} /> Ajouter un autre moyen de paiement
                                    </button>
                                    {markPaidEntries.length > 1 && Math.abs(markPaidEntriesSum - remainingDue) >= 0.01 && (
                                        <p className="text-xs text-red-400 font-semibold">
                                            La somme des moyens de paiement ({new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(markPaidEntriesSum)}) ne correspond pas au reste à payer ({new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(remainingDue)}).
                                        </p>
                                    )}
                                    {markPaidError && <p className="text-xs text-red-400 font-semibold">{markPaidError}</p>}
                                    <div className="flex gap-2">
                                        <button onClick={() => setShowMarkPaid(false)} className="flex-1 px-3 py-2 rounded-lg border border-border text-content-muted hover:text-content text-xs font-bold">Annuler</button>
                                        <button onClick={handleMarkPaid} disabled={!markPaidEntriesValid || markingPaid} className="flex-1 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-50">
                                            {markingPaid ? 'Validation...' : 'Confirmer'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="space-y-2">
                        <h4 className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Coordonnées client</h4>
                        <div className="space-y-1.5 text-sm text-content">
                            {/* Boutons « Appeler » (tel:) et « Écrire » (mailto:) : sur téléphone, le système ouvre l'application d'appel,
                                ou propose le choix de l'application de messagerie (Mail, Outlook, Gmail…). */}
                            {appt.crm_phone && (
                                <div className="flex items-center gap-2 flex-wrap">
                                    <p className="flex items-center gap-2"><Phone size={13} className="text-content-muted shrink-0" /> {appt.crm_phone}</p>
                                    <a href={`tel:${String(appt.crm_phone).replace(/[^\d+]/g, '')}`}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-xs font-bold transition-all">
                                        <Phone size={12} /> Appeler
                                    </a>
                                </div>
                            )}
                            {appt.crm_email && (
                                <div className="flex items-center gap-2 flex-wrap">
                                    <p className="flex items-center gap-2"><Mail size={13} className="text-content-muted shrink-0" /> {appt.crm_email}</p>
                                    <a href={`mailto:${appt.crm_email}`}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-xs font-bold transition-all">
                                        <Mail size={12} /> Écrire
                                    </a>
                                </div>
                            )}
                            {!appt.crm_phone && !appt.crm_email && <p className="text-content-muted italic text-xs">Aucune coordonnée renseignée</p>}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <h4 className="text-[10px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5"><MapPin size={12} /> Lieu du rendez-vous</h4>
                        <span className="inline-block text-[10px] font-black uppercase px-2 py-0.5 rounded-lg bg-surface-soft border border-border text-content-muted">
                            {appt.location_type === 'entreprise' ? "Dans l'entreprise" : 'Chez le client'}
                        </span>
                        {fullAddress ? (
                            <>
                                <p className="text-sm text-content">{fullAddress}</p>
                                {mapsUrl && (
                                    <a href={mapsUrl} target="_blank" rel="noreferrer"
                                        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-xs font-bold transition-all">
                                        <Navigation size={13} /> Ouvrir sur Maps
                                    </a>
                                )}
                            </>
                        ) : <p className="text-content-muted italic text-xs">Non renseignée</p>}
                    </div>

                    <LinkedNotesPanel kind="appointment" id={appt.id} />

                    <div className="space-y-2">
                        <h4 className="text-[10px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5"><UsersIcon size={12} /> Équipe sur cette mission</h4>
                        {(() => {
                            // Assignation en un clic (2026-10-16, inspiré de Jobber et Housecall Pro) : toutes les pastilles de salariés sont
                            // visibles, on coche ou décoche, puis un seul bouton enregistre et prévient les salariés. Plusieurs salariés possibles.
                            const equipeActuelle = (appt.employee_ids || []) as string[];
                            const modifiable = appt.status !== 'termine' && appt.status !== 'annule';
                            const change = assigningIds.length !== equipeActuelle.length || assigningIds.some((id) => !equipeActuelle.includes(id));
                            return (
                                <>
                                    {employees.length === 0 ? (
                                        <p className="text-xs text-content-muted italic">Aucun salarié enregistré.</p>
                                    ) : (
                                        <div className="flex flex-wrap gap-1.5">
                                            {employees.map((e) => {
                                                const actif = assigningIds.includes(e.id);
                                                return (
                                                    <button key={e.id} type="button" disabled={!modifiable} onClick={() => { setAssignError(''); toggleAssignId(e.id); }}
                                                        aria-pressed={actif}
                                                        className={`flex items-center gap-1.5 pl-1.5 pr-2.5 py-1 rounded-full text-[11px] font-bold border transition-all disabled:cursor-default ${actif ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black ${actif ? 'bg-white/25' : 'bg-content/10'}`}>
                                                            {(e.first_name || '?').charAt(0)}{(e.last_name || '').charAt(0)}
                                                        </span>
                                                        {e.first_name} {e.last_name}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {assigningIds.length === 0 && modifiable && (
                                        <p className="text-xs text-content-muted italic">Personne n'est assigné : touchez un ou plusieurs salariés.</p>
                                    )}
                                    {assignError && <p className="text-xs text-red-400 font-semibold">{assignError}</p>}
                                    {modifiable && change && (
                                        <button onClick={handleQuickAssign} disabled={assigning || assigningIds.length === 0}
                                            className="px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold disabled:opacity-50">
                                            {assigning ? 'Enregistrement…' : (isPendingRequest ? 'Assigner et confirmer le rendez-vous' : "Enregistrer l'équipe")}
                                        </button>
                                    )}
                                    {modifiable && change && (
                                        <p className="text-[11px] text-content-muted">Les salariés assignés sont prévenus par e-mail{isPendingRequest ? ', et le client reçoit la confirmation' : ''}.</p>
                                    )}
                                </>
                            );
                        })()}
                    </div>

                    {appt.notes && (
                        <div className="space-y-2">
                            <h4 className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Notes</h4>
                            <p className="text-sm text-content-muted whitespace-pre-wrap">{appt.notes}</p>
                        </div>
                    )}

                    {!isFullyPaid && !isPendingRequest && (
                        <div className="space-y-2">
                            {isClosedUnpaid ? (<>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest" htmlFor="autre-adresse-lien">Envoyer le lien à une autre adresse (facultatif)</label>
                                    <input id="autre-adresse-lien" type="email" value={autreAdresseLien} onChange={(e) => setAutreAdresseLien(e.target.value)}
                                        placeholder={appt.crm_email || 'prenom.nom@exemple.fr'}
                                        className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                    <p className="text-[11px] text-content-muted">Laissez vide pour utiliser l'adresse de la fiche client. La fiche n'est pas modifiée.</p>
                                </div>
                                <button onClick={handleSendPaymentReminder} disabled={sendingReminder}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-all border-warning-500/30 bg-warning-500/10 text-warning-400 hover:bg-warning-500/20">
                                    <Bell size={13} /> {sendingReminder ? 'Envoi...' : 'Relancer pour le paiement'}
                                </button>
                            </>) : (
                                <div className="grid grid-cols-2 gap-2">
                                    <button onClick={() => handleSendReminder('client')} disabled={sendingReminder}
                                        className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                                        <Bell size={13} /> {sendingReminder && rappelEnCours === 'client' ? 'Envoi...' : 'Rappel au client'}
                                    </button>
                                    <button onClick={() => handleSendReminder('employees')} disabled={sendingReminder}
                                        className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                                        <Bell size={13} /> {sendingReminder && rappelEnCours === 'employees' ? 'Envoi...' : 'Rappel aux salariés'}
                                    </button>
                                </div>
                            )}
                            {reminderMessage && (
                                <p className={`text-xs font-semibold ${reminderMessage.type === 'success' ? 'text-emerald-400' : 'text-red-400'}`}>{reminderMessage.text}</p>
                            )}
                        </div>
                    )}

                    
                    </div>
                    {onglet === 'client' && <ClientFicheTab appt={appt} />}
                    <div className={onglet === 'emails' ? 'space-y-2' : 'hidden'}>
                    <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                            <h4 className="text-[10px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5"><MessageSquare size={12} /> Historique message (client)</h4>
                            {appt.crm_email && (
                                <button onClick={() => setShowSendMessage(true)}
                                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-surface-soft border border-border text-content-muted hover:text-content text-[11px] font-bold transition-all">
                                    <Mail size={11} /> Message personnalisé
                                </button>
                            )}
                        </div>
                        {loadingEmails ? (
                            <div className="flex py-2"><Loader2 className="animate-spin text-content-muted" size={16} /></div>
                        ) : emails.length === 0 ? (
                            <p className="text-content-muted italic text-xs">Aucun email envoyé pour ce rendez-vous pour l'instant.</p>
                        ) : (
                            <div className="space-y-2">
                                {emails.map((e) => (
                                    <div key={e.id} className="p-2.5 rounded-xl bg-surface-soft border border-border">
                                        <button onClick={() => setPreviewEmail(e)} className="w-full text-left">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-bold text-content">{EMAIL_TYPE_LABELS[e.email_type] || e.subject}</span>
                                                <span className="text-[10px] text-content-muted shrink-0 ml-2">{new Date(e.sent_at).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                                            </div>
                                            <p className="text-[11px] text-content-muted mt-0.5">Envoyé à {e.recipient_email}</p>
                                        </button>
                                        <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-border">
                                            <button onClick={() => handleResendEmail(e.id)} disabled={resendingEmailId === e.id}
                                                className="flex items-center gap-1 text-[11px] font-bold text-content-muted hover:text-content disabled:opacity-50 transition-all">
                                                <Send size={10} /> {resendingEmailId === e.id ? 'Envoi...' : 'Renvoyer'}
                                            </button>
                                            {resendEmailResult?.id === e.id && (
                                                <span className={`text-[11px] font-semibold ${resendEmailResult.ok ? 'text-emerald-400' : 'text-red-400'}`}>{resendEmailResult.msg}</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    </div>
                </div>
                <div className="flex flex-wrap justify-between items-center gap-3 p-6 border-t border-border shrink-0">
                    <button onClick={onDelete} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-red-400 hover:bg-red-500/10 text-sm font-bold transition-all">
                        <Trash2 size={14} /> Supprimer
                    </button>
                    <div className="flex flex-wrap gap-3">
                        <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-border text-content-muted hover:text-content font-bold text-sm">Fermer</button>
                        {!isFullyPaid && (
                            <button onClick={onEdit} className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm">Modifier</button>
                        )}
                        {appt.status === 'planifie' && (
                            <button onClick={() => setShowStart(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-brand-500 text-brand-500 hover:bg-brand-500/10 font-bold text-sm transition-all">
                                <PlayCircle size={16} /> Commencer
                            </button>
                        )}
                        {(appt.status === 'planifie' || appt.status === 'en_cours') && (
                            <button onClick={() => setShowCompletion(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition-all">
                                <CheckCircle size={16} /> Terminer la prestation
                            </button>
                        )}
                    </div>
                </div>
            </motion.div>
            {showCompletion && (
                <AdminCompletionModal
                    appt={appt}
                    catalog={catalog}
                    onClose={() => setShowCompletion(false)}
                    onDone={async () => { setShowCompletion(false); onClose(); await onRefresh(); }}
                />
            )}
            {showStart && (
                <StartPrestationModal
                    appointmentId={appt.id}
                    onClose={() => setShowStart(false)}
                    onDone={async () => { setShowStart(false); onClose(); await onRefresh(); }}
                />
            )}
            {showSendMessage && (
                <SendCustomMessageModal
                    recipientLabel={contactLabel(appt)}
                    tokens={[
                        { token: '{{prenom}}', desc: 'Prénom du client' },
                        { token: '{{nom}}', desc: 'Nom (ou raison sociale) du client' },
                        { token: '{{date}}', desc: 'Date du rendez-vous' },
                        { token: '{{heure}}', desc: 'Heure du rendez-vous' },
                        { token: '{{entreprise}}', desc: 'Votre établissement' },
                        { token: '{{logo}}', desc: 'Votre logo' },
                    ]}
                    onClose={() => setShowSendMessage(false)}
                    onSend={async (subject, body) => {
                        const res = await moduleApi.sendPrestationCustomMessage(appt.id, { subject, body });
                        if (res?.success) loadEmails();
                        return res;
                    }}
                />
            )}
            {previewEmail && (
                <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[80] flex items-center justify-center p-4">
                    <div className="w-full max-w-lg bg-surface border border-border rounded-3xl max-h-[85vh] shadow-2xl flex flex-col overflow-hidden">
                        <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
                            <div className="min-w-0">
                                <h3 className="font-bold text-content text-sm truncate">{previewEmail.subject || EMAIL_TYPE_LABELS[previewEmail.email_type] || 'Email'}</h3>
                                <p className="text-xs text-content-muted mt-0.5 truncate">Envoyé à {previewEmail.recipient_email} le {new Date(previewEmail.sent_at).toLocaleString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                            </div>
                            <button onClick={() => setPreviewEmail(null)} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft shrink-0"><X size={18} /></button>
                        </div>
                        <div className="p-5 overflow-y-auto bg-surface-soft">
                            {previewEmail.html_body ? (
                                <div className="bg-white rounded-xl overflow-hidden border border-border">
                                    <iframe srcDoc={previewEmail.html_body} className="w-full" style={{ height: '55vh', border: 'none' }} sandbox="" title="Aperçu de l'email" />
                                </div>
                            ) : (
                                <p className="text-xs text-content-muted italic">Le contenu de cet email n'a pas été conservé (envoyé avant l'ajout de cet aperçu) — impossible de l'afficher ou de le renvoyer tel quel.</p>
                            )}
                        </div>
                        <div className="flex justify-between items-center gap-3 p-5 border-t border-border shrink-0">
                            {resendEmailResult?.id === previewEmail.id && (
                                <span className={`text-xs font-semibold ${resendEmailResult.ok ? 'text-emerald-400' : 'text-red-400'}`}>{resendEmailResult.msg}</span>
                            )}
                            <div className="flex gap-3 ml-auto">
                                <button onClick={() => setPreviewEmail(null)} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content text-sm font-bold">Fermer</button>
                                {previewEmail.html_body && (
                                    <button onClick={() => handleResendEmail(previewEmail.id)} disabled={resendingEmailId === previewEmail.id}
                                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">
                                        <Send size={14} /> {resendingEmailId === previewEmail.id ? 'Envoi...' : 'Renvoyer cet email'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export const PrestationPlanningModule: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [showSendDocumentModal, setShowSendDocumentModal] = useState(false);
    const [showSendLinkModal, setShowSendLinkModal] = useState(false);
    const [showNotifSettings, setShowNotifSettings] = useState(false);
    const [notifActiveModules, setNotifActiveModules] = useState<string[]>([]);
    const openNotifSettings = () => {
        setShowNotifSettings(true);
        authApi.getMyModules().then((modules: any) => {
            const names = (Array.isArray(modules) ? modules : [])
                .filter((m: any) => m.is_active === 1)
                .map((m: any) => m.module_name);
            setNotifActiveModules(names);
        }).catch((e) => console.error('Failed to load active modules', e));
    };
    const [sendLinkEmail, setSendLinkEmail] = useState('');
    const [sendingLink, setSendingLink] = useState(false);
    const [sendLinkMessage, setSendLinkMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const handleSendBookingLink = async () => {
        if (!sendLinkEmail.trim()) return;
        setSendingLink(true);
        setSendLinkMessage(null);
        try {
            await moduleApi.sendBookingLinkEmail({ email: sendLinkEmail.trim() });
            setSendLinkMessage({ type: 'success', text: 'Email envoyé avec succès.' });
            setSendLinkEmail('');
        } catch (e: any) {
            setSendLinkMessage({ type: 'error', text: e?.message || "Erreur lors de l'envoi." });
        } finally {
            setSendingLink(false);
        }
    };
    const [pendingRequestCount, setPendingRequestCount] = useState(0);
    useEffect(() => {
        if (user?.type !== 'client') return;
        const loadPendingCount = () => {
            moduleApi.getPrestationPendingRequestsCount().then((d: any) => setPendingRequestCount(d?.count || 0)).catch(() => {});
        };
        loadPendingCount();
        window.addEventListener('prestationBookingRequestProcessed', loadPendingCount);
        return () => window.removeEventListener('prestationBookingRequestProcessed', loadPendingCount);
    }, [user?.type]);

    // Cloche de notification du Planning : clique -> liste des demandes en attente -> clique sur une
    // demande -> ouvre sa fiche pour assigner un salarié et l'accepter, sans avoir à la chercher dans le
    // calendrier semaine par semaine (même donnée que "Demandes récurrentes" côté rendez-vous en attente,
    // accessible directement depuis l'Agenda).
    const [showPendingBell, setShowPendingBell] = useState(false);
    const [pendingRequestsList, setPendingRequestsList] = useState<Appointment[]>([]);
    const [loadingPendingList, setLoadingPendingList] = useState(false);
    const toggleBell = () => {
        const next = !showPendingBell;
        setShowPendingBell(next);
        if (next) {
            setLoadingPendingList(true);
            moduleApi.getPrestationPendingRequests().then((rows: any) => setPendingRequestsList(Array.isArray(rows) ? rows : []))
                .catch(() => setPendingRequestsList([]))
                .finally(() => setLoadingPendingList(false));
        }
    };
    const openPendingFromBell = (appt: Appointment) => {
        setShowPendingBell(false);
        setDate(appt.appointment_date);
        setViewMode('day');
        setDetailAppointment(appt);
    };

    // Bouton "RDV à venir" : liste (modale) des rendez-vous confirmés à venir, pour un aperçu rapide sans
    // naviguer semaine par semaine dans le calendrier.
    const [showUpcomingModal, setShowUpcomingModal] = useState(false);
    const [upcomingAppointments, setUpcomingAppointments] = useState<Appointment[]>([]);
    const [loadingUpcoming, setLoadingUpcoming] = useState(false);
    const openUpcomingModal = () => {
        setShowUpcomingModal(true);
        setLoadingUpcoming(true);
        moduleApi.getPrestationUpcomingAppointments().then((rows: any) => setUpcomingAppointments(Array.isArray(rows) ? rows : []))
            .catch(() => setUpcomingAppointments([]))
            .finally(() => setLoadingUpcoming(false));
    };
    const openUpcomingAppointment = (appt: Appointment) => {
        setShowUpcomingModal(false);
        setDate(appt.appointment_date);
        setViewMode('day');
        setDetailAppointment(appt);
    };
    // Deep-link depuis la cloche de la sidebar (?focus_date=...&focus_id=...) : ouvre directement le
    // Planning sur le jour de la demande en attente la plus proche, plutôt que de laisser l'établissement
    // la chercher manuellement semaine par semaine.
    const focusParams = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        const focusDate = params.get('focus_date');
        const focusId = params.get('focus_id');
        return focusDate && focusId ? { date: focusDate, id: focusId } : null;
    }, []);
    const [pendingFocusId, setPendingFocusId] = useState<string | null>(focusParams?.id || null);
    const [date, setDate] = useState(focusParams?.date || formatDateInput(new Date()));
    const [viewMode, setViewMode] = useState<'week' | 'day'>(focusParams ? 'day' : 'week');
    const [weeklyHours, setWeeklyHours] = useState<WeeklyHours>(defaultWeeklyHours());
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [catalog, setCatalog] = useState<PrestationCatalogItem[]>([]);
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [weekAppointments, setWeekAppointments] = useState<Record<string, Appointment[]>>({});
    const [loading, setLoading] = useState(true);
    const [modalState, setModalState] = useState<{ open: boolean; appointment: Appointment | null; date: string; presetTime?: string; presetEmployeeIds?: string[] }>({ open: false, appointment: null, date: date });
    const [showTrouverCreneau, setShowTrouverCreneau] = useState(false);
    // Filtre par salarié (2026-11-01) : liste vide = tout le monde. Mémorisé sur l'appareil pour retrouver la même vue.
    const [salariesFiltres, setSalariesFiltresEtat] = useState<string[]>(() => {
        try { const v = JSON.parse(localStorage.getItem('planning_prestation_filtre_salaries') || '[]'); return Array.isArray(v) ? v.map(String) : []; } catch { return []; }
    });
    const setSalariesFiltres = (ids: string[]) => {
        setSalariesFiltresEtat(ids);
        try { localStorage.setItem('planning_prestation_filtre_salaries', JSON.stringify(ids)); } catch { /* stockage indisponible : le filtre vaut pour cette visite seulement */ }
    };
    const [detailAppointment, setDetailAppointment] = useState<Appointment | null>(null);
    // Indicateur "note attachée" sur la bulle du planning (LinkedNotesPanel affiche le détail à
    // l'ouverture ; ici on veut juste savoir, pour toute la semaine visible, quels rendez-vous ont
    // au moins une note — un seul appel groupé plutôt qu'un par bulle.
    const [linkedNoteApptIds, setLinkedNoteApptIds] = useState<Set<string>>(new Set());
    const [quickNotesAppt, setQuickNotesAppt] = useState<Appointment | null>(null);
    const visibleApptIdsKey = useMemo(() => {
        const ids = viewMode === 'day' ? appointments.map((a) => a.id) : Object.values(weekAppointments).flat().map((a) => a.id);
        return Array.from(new Set(ids)).sort().join(',');
    }, [appointments, weekAppointments, viewMode]);
    useEffect(() => {
        const ids = visibleApptIdsKey ? visibleApptIdsKey.split(',') : [];
        notesApi.getLinkedIds('appointment', ids).then((r: any) => setLinkedNoteApptIds(new Set(r.ids || []))).catch(() => {});
    }, [visibleApptIdsKey]);

    useEffect(() => {
        (async () => {
            try {
                const [settings, emps, billing] = await Promise.all([
                    moduleApi.getPrestationPlanningSettings(),
                    moduleApi.getEmployes(),
                    moduleApi.getBillingSettings(),
                ]);
                if (settings?.weekly_hours) setWeeklyHours({ ...defaultWeeklyHours(), ...settings.weekly_hours });
                setEmployees((Array.isArray(emps) ? emps : []).filter((e: any) => !e.deleted_at));
                let parsed: any[] = [];
                try { parsed = JSON.parse(billing?.prestations_catalog || '[]'); } catch { parsed = []; }
                setCatalog((Array.isArray(parsed) ? parsed : []).map((p: any) => ({
                    id: String(p?.id || ''),
                    label: String(p?.label || ''),
                    unit_price_ttc: Number(p?.unit_price_ttc ?? 0),
                    tax_rate: Number(p?.tax_rate ?? 0),
                    show_tva: p?.show_tva !== false,
                    default_duration_minutes: Number(p?.default_duration_minutes ?? 0),
                    // Même lecture que l'écran Prestations : un catalogue jamais scopé est celui des professionnels.
                    client_type_scope: (['TOUS', 'PROFESSIONNEL', 'PRIVÉ'].includes(p?.client_type_scope) ? p.client_type_scope : 'PROFESSIONNEL') as PrestationCatalogItem['client_type_scope'],
                })).filter((p: PrestationCatalogItem) => p.label.length > 0));
            } catch (e) {
                console.error('Failed to load prestation planning setup', e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const loadDay = async (d: string) => {
        try {
            const rows = await moduleApi.getPrestationAppointmentsForDay(d);
            setAppointments(Array.isArray(rows) ? rows : []);
        } catch (e) {
            console.error('Failed to load appointments', e);
            setAppointments([]);
        }
    };

    const weekDates = useMemo(() => {
        const d = new Date(date + 'T00:00:00');
        const day = d.getDay();
        const diffToMonday = day === 0 ? -6 : 1 - day;
        const monday = new Date(d);
        monday.setDate(d.getDate() + diffToMonday);
        const dates: string[] = [];
        for (let i = 0; i < 7; i++) {
            const dd = new Date(monday);
            dd.setDate(monday.getDate() + i);
            dates.push(formatDateInput(dd));
        }
        return dates;
    }, [date]);

    const loadWeek = async (dates: string[]) => {
        try {
            const results = await Promise.all(dates.map((d) => moduleApi.getPrestationAppointmentsForDay(d)));
            const map: Record<string, Appointment[]> = {};
            dates.forEach((d, i) => { map[d] = Array.isArray(results[i]) ? results[i] : []; });
            setWeekAppointments(map);
        } catch (e) {
            console.error('Failed to load week appointments', e);
        }
    };

    const reloadCurrentView = async () => {
        if (viewMode === 'day') await loadDay(date);
        else await loadWeek(weekDates);
    };

    useEffect(() => {
        if (viewMode === 'day') loadDay(date);
        else loadWeek(weekDates);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, viewMode]);

    // Une fois le jour ciblé chargé, ouvre directement la demande visée par le deep-link — puis nettoie
    // l'URL pour qu'un rafraîchissement de la page n'essaie pas de la rouvrir indéfiniment.
    useEffect(() => {
        if (!pendingFocusId) return;
        const found = appointments.find((a) => a.id === pendingFocusId);
        if (found) {
            setDetailAppointment(found);
            setPendingFocusId(null);
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [appointments, pendingFocusId]);

    const shiftPeriod = (direction: 1 | -1) => {
        const deltaDays = (viewMode === 'week' ? 7 : 1) * direction;
        const d = new Date(date + 'T00:00:00');
        d.setDate(d.getDate() + deltaDays);
        setDate(formatDateInput(d));
    };

    const dayKey = dayKeyForDate(date);
    const dayHours = weeklyHours[dayKey];
    const isDayClosed = dayHours.closed;
    const openingMinutes = timeToMinutes(dayHours.opening);
    const closingMinutes = timeToMinutes(dayHours.closing);

    const weekAxis = useMemo(() => {
        const openDays = weekDates.map((d) => weeklyHours[dayKeyForDate(d)]).filter((h) => !h.closed);
        if (openDays.length === 0) return { opening: timeToMinutes('08:00'), closing: timeToMinutes('20:00') };
        const opening = Math.min(...openDays.map((h) => timeToMinutes(h.opening)));
        const closing = Math.max(...openDays.map((h) => timeToMinutes(h.closing)));
        return { opening, closing: Math.max(closing, opening + 60) };
    }, [weekDates, weeklyHours]);

    const axisOpeningMinutes = viewMode === 'day' ? openingMinutes : weekAxis.opening;
    const axisClosingMinutes = viewMode === 'day' ? closingMinutes : weekAxis.closing;
    const axisTotalMinutes = Math.max(60, axisClosingMinutes - axisOpeningMinutes);
    const axisTotalHeight = (axisTotalMinutes / 60) * PX_PER_HOUR;

    const hourMarks = useMemo(() => {
        const marks: number[] = [];
        for (let m = axisOpeningMinutes; m <= axisClosingMinutes; m += 60) marks.push(m);
        return marks;
    }, [axisOpeningMinutes, axisClosingMinutes]);

    const weekRangeLabel = () => {
        const start = new Date(weekDates[0] + 'T00:00:00');
        const end = new Date(weekDates[6] + 'T00:00:00');
        const startStr = start.toLocaleDateString('fr-FR', { day: 'numeric', month: start.getMonth() === end.getMonth() ? undefined : 'long' });
        const endStr = end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
        return `${startStr} – ${endStr}`;
    };

    const openNewAppointment = (dayDate: string) => {
        setModalState({ open: true, appointment: null, date: dayDate });
    };

    const openEditAppointment = (appt: Appointment) => {
        setDetailAppointment(null);
        setModalState({ open: true, appointment: appt, date: appt.appointment_date });
    };

    const handleDeleteFromDetail = async () => {
        if (!detailAppointment) return;
        await moduleApi.deletePrestationAppointment(detailAppointment.id);
        setDetailAppointment(null);
        await reloadCurrentView();
    };

    const handleSaveAppointment = async (payload: any) => {
        if (modalState.appointment) {
            const res: any = await moduleApi.updatePrestationAppointment(modalState.appointment.id, payload);
            if (res?.was_pending_request) {
                window.dispatchEvent(new Event('prestationBookingRequestProcessed'));
            }
        } else {
            const res: any = await moduleApi.createPrestationAppointment(payload);
            if (Array.isArray(res?.recurrence_skipped_dates) && res.recurrence_skipped_dates.length > 0) {
                alert(`Rendez-vous créé. ${res.recurrence_skipped_dates.length} occurrence(s) n'ont pas pu être créées à cause d'un conflit avec un salarié déjà assigné ce jour-là : ${res.recurrence_skipped_dates.join(', ')}`);
            }
            // Paiement demandé au client : le rendez-vous reste « en attente de paiement » jusqu'au règlement.
            if (res?.payment_warning) {
                alert(`Rendez-vous créé. ${res.payment_warning}`);
            } else if (res?.payment_requested) {
                alert("Rendez-vous créé. Un lien de paiement vient d'être envoyé au client : le rendez-vous sera confirmé, et le client prévenu par e-mail, dès réception du paiement.");
            }
        }
        setModalState({ open: false, appointment: null, date });
        await reloadCurrentView();
    };

    const handleDeleteAppointment = async () => {
        if (!modalState.appointment) return;
        await moduleApi.deletePrestationAppointment(modalState.appointment.id);
        setModalState({ open: false, appointment: null, date });
        await reloadCurrentView();
    };

    if (loading) {
        return (
            <div className="py-20 text-center text-content-muted flex flex-col items-center gap-2">
                <Loader2 size={28} className="animate-spin" />
                <span className="text-sm">Chargement du planning...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <header className="space-y-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-brand-500/15 flex items-center justify-center text-brand-500">
                            <CalendarClock size={22} />
                        </div>
                        <div>
                            <h1 className="text-2xl font-black text-content">Planning</h1>
                            <p className="text-sm text-content-muted mt-1 capitalize">{viewMode === 'day' ? formatDateHuman(date) : weekRangeLabel()}</p>
                            {pendingRequestCount > 0 && (
                                <p className="text-xs text-warning-500 font-bold mt-1">{pendingRequestCount} demande{pendingRequestCount > 1 ? 's' : ''} de réservation à confirmer</p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {user?.type === 'client' && (
                            <>
                                <div className="relative">
                                    <button onClick={toggleBell} aria-label="Demandes de réservation en attente"
                                        className="relative p-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-all">
                                        <Bell size={16} />
                                        {pendingRequestCount > 0 && (
                                            <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-black flex items-center justify-center shadow-lg shadow-red-500/40 animate-pulse">
                                                {pendingRequestCount > 99 ? '99+' : pendingRequestCount}
                                            </span>
                                        )}
                                    </button>
                                    {showPendingBell && (
                                        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-surface border border-border rounded-2xl shadow-2xl z-40">
                                            <div className="px-4 py-3 border-b border-border">
                                                <p className="text-xs font-black uppercase tracking-wider text-content-muted">Demandes en attente</p>
                                            </div>
                                            {loadingPendingList ? (
                                                <div className="flex justify-center py-6"><Loader2 size={18} className="animate-spin text-content-muted" /></div>
                                            ) : pendingRequestsList.length === 0 ? (
                                                <p className="px-4 py-6 text-xs text-content-muted text-center">Aucune demande en attente.</p>
                                            ) : (
                                                pendingRequestsList.map((appt) => (
                                                    <button key={appt.id} onClick={() => openPendingFromBell(appt)}
                                                        className="w-full text-left px-4 py-3 border-b border-border last:border-b-0 hover:bg-surface-soft transition-colors">
                                                        <p className="text-sm font-bold text-content truncate">{contactLabel(appt)}</p>
                                                        <p className="text-xs text-content-muted mt-0.5">{formatDateHuman(appt.appointment_date)} à {appt.start_time} — {appt.prestation_label}</p>
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                                <MenuDeroulant
                                    bouton={<><Send size={14} /> Envoyer</>}
                                    elements={[
                                        { icone: <Link2 size={16} />, titre: 'Un lien de prise de rendez-vous', description: 'Le client reçoit un e-mail et choisit lui-même son créneau.', onClick: () => { setShowSendLinkModal(true); setSendLinkMessage(null); } },
                                        { icone: <FileText size={16} />, titre: 'Un document ou une démarche', description: 'Formulaire, contrat… envoyé par e-mail à remplir ou signer.', onClick: () => setShowSendDocumentModal(true) },
                                        { icone: <ClipboardList size={16} />, titre: 'Voir les démarches déjà envoyées', description: 'Historique des envois et de leur suivi.', separe: true, onClick: () => navigate('/demandes-recurrentes') },
                                    ]}
                                />
                            </>
                        )}
                        <button onClick={() => setShowTrouverCreneau(true)} className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border text-content hover:bg-surface-soft text-xs font-bold transition-colors">
                            <Clock size={14} /> Trouver un créneau
                        </button>
                        <button onClick={() => openNewAppointment(date)} disabled={viewMode === 'day' && isDayClosed}
                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold shadow-lg shadow-brand-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                            <Plus size={16} /> Nouveau
                        </button>
                        {user?.type === 'client' && (
                            <MenuDeroulant
                                bouton={<MoreHorizontal size={16} />}
                                etiquetteAccessible="Plus d'options"
                                avecFleche={false}
                                elements={[
                                    { icone: <CalendarClock size={16} />, titre: 'Rendez-vous à venir', description: 'La liste des prochains rendez-vous.', onClick: openUpcomingModal },
                                    { icone: <SettingsIcon size={16} />, titre: 'Paramètres du planning', description: 'Horaires, notifications et rappels.', onClick: openNotifSettings },
                                ]}
                            />
                        )}
                    </div>
                </div>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-1 bg-surface-soft border border-border rounded-xl p-1">
                            <button onClick={() => setViewMode('week')} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${viewMode === 'week' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>Semaine</button>
                            <button onClick={() => setViewMode('day')} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${viewMode === 'day' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>Jour</button>
                        </div>
                        <button onClick={() => shiftPeriod(-1)} aria-label="Période précédente" className="p-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-all"><ChevronLeft size={16} /></button>
                        <input type="date" value={viewMode === 'week' ? weekDates[0] : date} onChange={(e) => setDate(e.target.value)}
                            className="bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                        <button onClick={() => shiftPeriod(1)} aria-label="Période suivante" className="p-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-all"><ChevronRight size={16} /></button>
                        <button onClick={() => setDate(formatDateInput(new Date()))} className="px-3 py-2.5 rounded-xl border border-border text-content-muted hover:text-content text-xs font-bold hover:bg-surface-soft transition-all">Aujourd'hui</button>
                    </div>
                    {employees.length > 0 && <FiltreSalaries employees={employees} selection={salariesFiltres} onChange={setSalariesFiltres} />}
                </div>
            </header>

            {employees.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-2xl bg-surface/40">
                    <UsersIcon size={32} className="mx-auto mb-2 text-content-muted opacity-40" />
                    <p className="text-content-muted text-sm">Configurez d'abord vos employés dans "Postes &amp; Employés" pour planifier des rendez-vous.</p>
                </div>
            ) : viewMode === 'day' && isDayClosed ? (
                <div className="text-center py-16 border border-dashed border-border rounded-2xl bg-surface/40">
                    <Clock size={32} className="mx-auto mb-2 text-content-muted opacity-40" />
                    <p className="text-content-muted text-sm">Fermé le {DAY_LABELS[dayKey].toLowerCase()} — modifiez les horaires dans Paramètres si besoin.</p>
                </div>
            ) : (
                <div className="rounded-2xl border border-border bg-surface overflow-hidden">
                    <div className="overflow-x-auto">
                        <div className="flex min-w-max">
                            <div className="w-16 shrink-0 border-r border-border">
                                <div className="h-12 border-b border-border" />
                                <div className="relative" style={{ height: axisTotalHeight }}>
                                    {hourMarks.map((m) => (
                                        <div key={m} className="absolute left-0 right-0 text-[10px] text-content-muted px-1 -translate-y-1/2"
                                            style={{ top: ((m - axisOpeningMinutes) / axisTotalMinutes) * axisTotalHeight }}>
                                            {minutesToTime(m)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {(viewMode === 'day' ? [date] : weekDates).map((d) => {
                                const dHours = weeklyHours[dayKeyForDate(d)];
                                const dAppointmentsBruts = viewMode === 'day' ? appointments : (weekAppointments[d] || []);
                                // Un rendez-vous sans salarié (demande à confirmer, attente de paiement) reste toujours visible : le filtre ne doit jamais cacher une demande.
                                const dAppointments = salariesFiltres.length > 0 ? dAppointmentsBruts.filter((a) => (a.employee_ids || []).length === 0 || a.employee_ids.some((id) => salariesFiltres.includes(id))) : dAppointmentsBruts;
                                const { packed, laneCount } = packAppointments(dAppointments);
                                return (
                                    <div key={d} className={viewMode === 'day' ? 'flex-1 min-w-[320px] border-r border-border last:border-r-0' : 'flex-1 min-w-[150px] border-r border-border last:border-r-0'}>
                                        <div className="h-12 border-b border-border flex items-center justify-center px-2">
                                            <span className="text-xs font-bold text-content truncate">{viewMode === 'day' ? formatDateHuman(d) : dayShortLabel(d)}</span>
                                        </div>
                                        <div
                                            className="relative"
                                            style={{ height: axisTotalHeight }}
                                            onClick={() => !dHours.closed && openNewAppointment(d)}
                                        >
                                            {dHours.closed ? (
                                                <div className="absolute inset-0 flex items-center justify-center text-[11px] text-content-muted italic">Fermé</div>
                                            ) : (
                                                <>
                                                    {hourMarks.map((m) => (
                                                        <div key={m} className="absolute left-0 right-0 border-t border-border/50"
                                                            style={{ top: ((m - axisOpeningMinutes) / axisTotalMinutes) * axisTotalHeight }} />
                                                    ))}
                                                    {packed.map(({ appt, lane }) => {
                                                        const startMin = timeToMinutes(appt.start_time);
                                                        const dureeMin = Math.round(appt.computed_duration_minutes);
                                                        const endTime = minutesToTime(startMin + dureeMin);
                                                        const top = Math.max(0, ((startMin - axisOpeningMinutes) / axisTotalMinutes) * axisTotalHeight);
                                                        const minHeight = Math.max(28, (appt.computed_duration_minutes / axisTotalMinutes) * axisTotalHeight);
                                                        const width = 100 / laneCount;
                                                        const left = lane * width;
                                                        const isPendingRequest = appt.status === 'demande';
                                                        // Paiement obligatoire pas encore confirmé (2026-09-26) : distinct de "demande" — ici rien
                                                        // n'est à faire côté établissement, on attend seulement le client (ou une relance).
                                                        const isPendingPayment = appt.status === 'attente_paiement';
                                                        // Affichage lisible du contenu du rendez-vous (2026-09-06, Emeni : « l'affichage de la
                                                        // prestation dans le planning est incompréhensible ») : une ligne par prestation avec sa
                                                        // quantité, et le personnel isolé en bas — plutôt qu'un seul libellé collé, séparé par des
                                                        // virgules, où l'on ne distinguait ni les prestations entre elles ni les quantités.
                                                        const lignes = (appt.catalog_items && appt.catalog_items.length > 0)
                                                            ? appt.catalog_items.map((it) => ({ label: it.label, quantity: Number(it.quantity || 1) }))
                                                            : [{ label: appt.prestation_label, quantity: Number(appt.quantity || 1) }];
                                                        // La hauteur du bloc suit la durée : on n'affiche que ce qui tient, le reste est résumé
                                                        // par « +N autres » (le détail complet reste dans la fiche, au clic).
                                                        const hauteurEntete = isPendingRequest || isPendingPayment || appt.status === 'termine' || appt.status === 'en_cours' ? 50 : 36;
                                                        const maxLignes = Math.max(1, Math.floor((minHeight - hauteurEntete - 15) / 12));
                                                        const lignesVisibles = lignes.length > maxLignes ? lignes.slice(0, Math.max(1, maxLignes - 1)) : lignes;
                                                        const lignesRestantes = lignes.length - lignesVisibles.length;
                                                        const salaries = isPendingRequest || isPendingPayment ? '' : employeeNamesLabel(appt, employees);
                                                        // Couleur(s) du ou des salariés affectés : la barre de gauche les identifie, l'état reste écrit en toutes lettres.
                                                        const couleursEquipe = isPendingRequest || isPendingPayment ? [] : (appt.employee_ids || [])
                                                            .map((id) => employees.find((x) => x.id === id)).filter((x): x is Employee => !!x).map((x) => couleurSalarie(x));
                                                        // Carte de rendez-vous refaite (2026-10-16, demande d'Emeni : « visuellement, ce n'est pas joli ») : fond clair, barre de
                                                        // couleur à gauche selon l'état, texte lisible (plus de blanc sur aplat), pastille d'état en toutes lettres.
                                                        const etat = isPendingRequest
                                                            ? { label: 'À confirmer', barre: 'bg-warning-500', fond: 'bg-warning-500/10 hover:bg-warning-500/20', texte: 'text-warning-600' }
                                                            : isPendingPayment
                                                            ? { label: 'En attente de paiement', barre: 'bg-slate-400', fond: 'bg-slate-500/10 hover:bg-slate-500/20', texte: 'text-slate-500' }
                                                            : appt.status === 'termine'
                                                            ? { label: 'Terminé', barre: 'bg-green-500', fond: 'bg-green-500/10 hover:bg-green-500/20', texte: 'text-green-600' }
                                                            : appt.status === 'en_cours'
                                                            ? { label: 'En cours', barre: 'bg-brand-600', fond: 'bg-brand-500/15 hover:bg-brand-500/25', texte: 'text-brand-600' }
                                                            : { label: '', barre: 'bg-brand-600', fond: 'bg-brand-500/10 hover:bg-brand-500/20', texte: 'text-brand-600' };
                                                        return (
                                                            <button
                                                                key={appt.id}
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); setDetailAppointment(appt); }}
                                                                className={`absolute z-10 hover:z-20 overflow-hidden rounded-lg border border-border/60 text-left shadow-sm transition-[filter,box-shadow] hover:shadow-md ${couleursEquipe.length > 0 ? 'hover:brightness-95' : etat.fond}`}
                                                                style={{ top, minHeight, left: `${left}%`, width: `calc(${width}% - 4px)`, ...(couleursEquipe.length > 0 ? { backgroundColor: `color-mix(in srgb, ${couleursEquipe[0]} 11%, var(--bg-card))` } : {}) }}
                                                            >
                                                                {couleursEquipe.length > 0 ? (
                                                                    <span aria-hidden className="absolute left-0 top-0 bottom-0 w-1.5 flex flex-col">
                                                                        {couleursEquipe.map((c, i) => <i key={i} className="flex-1" style={{ backgroundColor: c }} />)}
                                                                    </span>
                                                                ) : (
                                                                    <span aria-hidden className={`absolute left-0 top-0 bottom-0 w-1 ${etat.barre}`} />
                                                                )}
                                                                {linkedNoteApptIds.has(appt.id) && (
                                                                    <span title="Voir les notes attachées"
                                                                        onClick={(e) => { e.stopPropagation(); setQuickNotesAppt(appt); }}
                                                                        className="absolute top-1 right-1 text-content-muted hover:text-content cursor-pointer">
                                                                        <NotebookText size={11} />
                                                                    </span>
                                                                )}
                                                                {minHeight < 48 ? (
                                                                    // Rendez-vous court : une seule ligne (heure + nom), comme dans Google Agenda ou Fresha, plutôt que du texte écrasé.
                                                                    <div className="pl-2.5 pr-1.5 py-0.5 flex items-center gap-1.5 min-w-0">
                                                                        <span className="text-[10px] font-semibold text-content-muted tabular-nums shrink-0">{appt.start_time}</span>
                                                                        <span className="text-[11px] font-bold text-content truncate">{contactLabel(appt)}</span>
                                                                        {etat.label && <span className={`ml-auto shrink-0 text-[9px] font-black uppercase ${etat.texte}`}>{etat.label}</span>}
                                                                    </div>
                                                                ) : (
                                                                <div className="pl-2.5 pr-1.5 py-1">
                                                                    <div className="flex items-baseline gap-1.5">
                                                                        <p className="text-[11px] font-bold text-content leading-snug break-words min-w-0 flex-1">{contactLabel(appt)}</p>
                                                                        {!!appt.formula_occurrence_number && (
                                                                            <span className="shrink-0 text-[9px] font-black uppercase tracking-wide text-content-muted bg-content/10 rounded px-1 py-px">
                                                                                P{appt.formula_occurrence_number}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <p className="text-[10px] font-semibold text-content-muted leading-snug tabular-nums flex items-baseline justify-between gap-1">
    <span>{dureeMin > 0 ? `${appt.start_time} - ${endTime}` : `${appt.start_time} · durée à définir`}</span>
    {appt.status !== 'demande' && appt.status !== 'attente_paiement' && (
        <span className={`shrink-0 text-[9px] font-black uppercase tracking-wide ${appt.payment_status === 'encaisse' ? 'text-green-600' : 'text-content-muted'}`}>
            {appt.payment_status === 'encaisse' ? 'Payé' : 'À encaisser'}
        </span>
    )}
</p>
                                                                    {etat.label && (
                                                                        <p className={`text-[9px] font-black uppercase tracking-wide leading-snug ${etat.texte}`}>{etat.label}</p>
                                                                    )}
                                                                    {appt.commercial_nom && (
                                                                        <p className="text-[10px] font-semibold text-content-muted leading-snug truncate" title={`Pris par ${appt.commercial_nom}`}>Pris par {appt.commercial_nom}</p>
                                                                    )}
                                                                    <div className="mt-1 pt-1 border-t border-border/60 space-y-px">
                                                                        {lignesVisibles.map((l, i) => (
                                                                            <p key={i} className="text-[10px] text-content leading-[13px] flex gap-1" title={`${l.quantity} × ${l.label}`}>
                                                                                <span className="font-black shrink-0 tabular-nums">{l.quantity}×</span>
                                                                                <span className="truncate">{l.label}</span>
                                                                            </p>
                                                                        ))}
                                                                        {lignesRestantes > 0 && (
                                                                            <p className="text-[10px] italic text-content-muted leading-[13px]">+{lignesRestantes} autre{lignesRestantes > 1 ? 's' : ''}</p>
                                                                        )}
                                                                    </div>
                                                                    <p className="mt-1 pt-1 border-t border-border/60 text-[10px] text-content font-semibold leading-snug flex items-center gap-1">
                                                                        {couleursEquipe.length > 0
                                                                            ? <span aria-hidden className="flex shrink-0 -space-x-0.5">{couleursEquipe.slice(0, 4).map((c, i) => <i key={i} className="w-2 h-2 rounded-full ring-1 ring-surface" style={{ backgroundColor: c }} />)}</span>
                                                                            : <UsersIcon size={10} className="shrink-0 text-content-muted" />}
                                                                        <span className="truncate" title={salaries || undefined}>{salaries ? (salaries.includes(',') ? `${salaries.split(',')[0].trim()} +${salaries.split(',').length - 1}` : salaries) : 'Aucun salarié assigné'}</span>
                                                                    </p>
                                                                </div>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {showSendDocumentModal && (
                <SendDocumentByEmailModal onClose={() => setShowSendDocumentModal(false)} onSent={() => {}} />
            )}

            {showSendLinkModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                    <div className="bg-surface border border-border rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
                        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                            <h3 className="text-lg font-bold text-content flex items-center gap-2"><Link2 size={18} className="text-brand-500" /> Envoyer un lien de rendez-vous</h3>
                            <button onClick={() => setShowSendLinkModal(false)} className="text-content-muted hover:text-content"><X size={20} /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-content-muted">Le client recevra un email avec un lien vers votre page de rendez-vous en ligne, où apparaissent tous les types de rendez-vous que vous avez configurés (Paramètres &gt; Démarches client, case "Faire apparaître dans le widget"). Il choisit le type, remplit ses informations — sa fiche client existante est automatiquement mise à jour si l'adresse email correspond déjà à un contact.</p>
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Email du client</label>
                                <input type="email" autoFocus value={sendLinkEmail} onChange={(e) => setSendLinkEmail(e.target.value)}
                                    placeholder="client@exemple.com"
                                    className="w-full bg-surface-soft border border-border rounded-xl px-4 py-3 text-sm text-content outline-none focus:border-brand-500 transition-colors" />
                            </div>
                            {sendLinkMessage && (
                                <p className={`text-xs font-semibold ${sendLinkMessage.type === 'success' ? 'text-emerald-500' : 'text-red-400'}`}>{sendLinkMessage.text}</p>
                            )}
                            <button onClick={handleSendBookingLink} disabled={sendingLink || !sendLinkEmail.trim()}
                                className="w-full px-5 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold disabled:opacity-50 transition-all">
                                {sendingLink ? 'Envoi...' : 'Envoyer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showNotifSettings && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                    <div className="bg-surface border border-border rounded-2xl w-full max-w-3xl max-h-[85vh] shadow-2xl overflow-hidden flex flex-col">
                        <div className="px-6 py-4 border-b border-border flex items-center justify-between shrink-0">
                            <h3 className="text-lg font-bold text-content flex items-center gap-2"><SettingsIcon size={18} className="text-brand-500" /> E-mails du planning</h3>
                            <button onClick={() => setShowNotifSettings(false)} className="text-content-muted hover:text-content"><X size={20} /></button>
                        </div>
                        <div className="overflow-y-auto p-6">
                            <PrestationNotificationsEnLignes activeModules={notifActiveModules} />
                        </div>
                    </div>
                </div>
            )}

            {showUpcomingModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                    <div className="bg-surface border border-border rounded-2xl w-full max-w-lg max-h-[80vh] shadow-2xl overflow-hidden flex flex-col">
                        <div className="px-6 py-4 border-b border-border flex items-center justify-between shrink-0">
                            <h3 className="text-lg font-bold text-content flex items-center gap-2"><CalendarClock size={18} className="text-brand-500" /> Rendez-vous à venir</h3>
                            <button onClick={() => setShowUpcomingModal(false)} className="text-content-muted hover:text-content"><X size={20} /></button>
                        </div>
                        <div className="overflow-y-auto">
                            {loadingUpcoming ? (
                                <div className="flex justify-center py-10"><Loader2 size={22} className="animate-spin text-content-muted" /></div>
                            ) : upcomingAppointments.length === 0 ? (
                                <p className="px-6 py-10 text-sm text-content-muted text-center">Aucun rendez-vous confirmé à venir.</p>
                            ) : (
                                upcomingAppointments.map((appt) => (
                                    <button key={appt.id} onClick={() => openUpcomingAppointment(appt)}
                                        className="w-full text-left px-6 py-4 border-b border-border last:border-b-0 hover:bg-surface-soft transition-colors">
                                        <div className="flex items-center justify-between gap-3">
                                            <p className="text-sm font-bold text-content truncate">{contactLabel(appt)}</p>
                                            <p className="text-xs text-content-muted shrink-0 capitalize">{formatDateHuman(appt.appointment_date)}</p>
                                        </div>
                                        <p className="text-xs text-content-muted mt-0.5">{appt.start_time} — {appt.prestation_label}</p>
                                        <p className="text-xs text-content-muted mt-0.5">{employeeNamesLabel(appt, employees) || 'Aucun salarié assigné'}</p>
                                        {appt.commercial_nom && <p className="text-xs text-content-muted mt-0.5">Pris par {appt.commercial_nom}</p>}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            <AnimatePresence>
                {showTrouverCreneau && (
                    <TrouverCreneauModal
                        employees={employees}
                        onClose={() => setShowTrouverCreneau(false)}
                        onPick={(s) => { setShowTrouverCreneau(false); setDate(s.date); setViewMode('day'); setModalState({ open: true, appointment: null, date: s.date, presetTime: s.start_time, presetEmployeeIds: s.employee_ids }); }}
                    />
                )}
                {modalState.open && (
                    <AppointmentModal
                        initial={modalState.appointment}
                        catalog={catalog}
                        employees={employees}
                        date={modalState.date}
                        weeklyHours={weeklyHours}
                        presetTime={modalState.presetTime}
                        presetEmployeeIds={modalState.presetEmployeeIds}
                        onClose={() => setModalState({ open: false, appointment: null, date })}
                        onSave={handleSaveAppointment}
                        onDelete={modalState.appointment ? handleDeleteAppointment : undefined}
                    />
                )}
                {detailAppointment && (
                    <AppointmentDetailModal
                        appt={detailAppointment}
                        employees={employees}
                        catalog={catalog}
                        onClose={() => setDetailAppointment(null)}
                        onEdit={() => openEditAppointment(detailAppointment)}
                        onDelete={handleDeleteFromDetail}
                        onRefresh={reloadCurrentView}
                    />
                )}
                {quickNotesAppt && (
                    <LinkedNotesQuickModal kind="appointment" id={quickNotesAppt.id} label={contactLabel(quickNotesAppt)} onClose={() => setQuickNotesAppt(null)} />
                )}
            </AnimatePresence>
        </div>
    );
};
